import { MAX_INT4 } from "../../constants/db-limits";
import { ArenaOnboardingStatusSchema } from "../../shared/arena-onboarding-types";
import { ArenaMatchOpponentDtoSchema } from "../../shared/arena-opponent-types";
import { FixedDrawdownEliminationConfigSchema } from "../paper-trading/paper-trading-config";
import { SPIN_AND_GO_CATEGORY_SEATS } from "../spin-and-go";
import { ARENA_MAX_PAIR_SYMBOLS } from "../trade-to-earn/admin-trade-to-earn-campaign.dto";
import { NonNegativeDecimalStringSchema } from "./decimal-string";
import { ArenaEntryTierSchema, type ArenaEntryTierDto } from "./entry-tier";
import { ArenaRakeBpsSchema } from "./rake";
import { Decimal } from "decimal.js";
import { z } from "zod";

export * from "./battle-royale";
export * from "./cancelled-competition";
export * from "./competition-blueprint.dto";
export * from "./kol-attribution";
export * from "./private-competition.dto";
export * from "./prize-split";
export * from "./rake";
export * from "./referral.dto";
export * from "./kol-manager.dto";
export * from "./clan.dto";
export * from "./external-reward";
export * from "./arena-chat.dto";
export * from "./arena-feed.dto";
export * from "./arena-affiliate.dto";
export * from "./arena-qualification-questions";
export * from "./arena-qualification.dto";
export * from "./big-arena-competition.dto";
export * from "./arena-tournament.dto";
export * from "./arena-survey.dto";
export * from "./arena-market-favorites.dto";
export * from "./arena-support-chat.dto";
export * from "./arena-support-agent.dto";
export * from "./player-profile.dto";
export * from "./arena-friends.dto";
export * from "./opponent-activity.dto";
export * from "./battle-record.dto";
export * from "./battle-record-upgrade";
export * from "./arena-opening-move.dto";
export * from "../../shared/arena-onboarding-types";
export * from "../../shared/arena-opponent-types";

/** Scope tabs for the Arena general leaderboard. "ranked" = staked competitions,
 *  "free" = casual (no buy-in), "all" = both. */
export const ArenaGeneralLeaderboardScopeSchema = z.enum(["ranked", "free", "all"]);
export type ArenaGeneralLeaderboardScope = z.infer<typeof ArenaGeneralLeaderboardScopeSchema>;

/** Rolling time window (filters epoch `finalizedAt` + reward `createdAt`). */
export const ArenaGeneralLeaderboardWindowSchema = z.enum(["7d", "30d", "all"]);
export type ArenaGeneralLeaderboardWindow = z.infer<typeof ArenaGeneralLeaderboardWindowSchema>;

/**
 * Ranking order. "wins" = wins, then rewards; "rewards" = rewards won, then wins, over winners only
 * (rewardsWonUsdt > 0). Ties break on hubUserId.
 */
export const ArenaGeneralLeaderboardSortSchema = z.enum(["wins", "rewards"]);
export type ArenaGeneralLeaderboardSort = z.infer<typeof ArenaGeneralLeaderboardSortSchema>;

/** One row of the Arena general leaderboard. `rewardsWonUsdt` is a decimal
 *  string (post-rake USDT credited); `0` for the Free tab. `winRate` is a
 *  percentage 0–100. Display chain: `nickname` → `Player #<hubUserId>`. */
export const ArenaGeneralLeaderboardEntrySchema = z.object({
  rank: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  rewardsWonUsdt: NonNegativeDecimalStringSchema,
  wins: z.number().int().nonnegative(),
  losses: z.number().int().nonnegative(),
  played: z.number().int().nonnegative(),
  winRate: z.number().min(0).max(100),
  /** Current win streak: consecutive wins from the most recent competition backward. */
  streak: z.number().int().nonnegative(),
});
export type ArenaGeneralLeaderboardEntry = z.infer<typeof ArenaGeneralLeaderboardEntrySchema>;

/**
 * Deepest page a leaderboard will serve. The server caches each page under its own key, so an
 * unbounded `?page=` is an unbounded set of cache entries, each paid for with one full aggregate.
 * At the 100-row ceiling on `size` this still reaches 100k entrants, far past any real board.
 */
export const ARENA_LEADERBOARD_MAX_PAGE = 1_000;

/** Query string for `GET /v1/cede-arena/leaderboard`. Page is 0-based. */
export const GetArenaGeneralLeaderboardQuerySchema = z.object({
  scope: ArenaGeneralLeaderboardScopeSchema.optional().default("ranked"),
  window: ArenaGeneralLeaderboardWindowSchema.optional().default("all"),
  sort: ArenaGeneralLeaderboardSortSchema.optional().default("wins"),
  // Reject an explicit blank `?page=` (which coerces to 0) — only an omitted param defaults.
  page: z
    .preprocess((v) => (v === "" ? NaN : v), z.coerce.number().int().nonnegative().max(ARENA_LEADERBOARD_MAX_PAGE))
    .optional()
    .default(0),
  size: z.coerce.number().int().positive().max(100).optional().default(25),
});
export type GetArenaGeneralLeaderboardQueryDto = z.infer<typeof GetArenaGeneralLeaderboardQuerySchema>;

/** Response payload for `GET /v1/cede-arena/leaderboard`. */
export const GetArenaGeneralLeaderboardResponseSchema = z.object({
  entries: ArenaGeneralLeaderboardEntrySchema.array(),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type GetArenaGeneralLeaderboardResponse = z.infer<typeof GetArenaGeneralLeaderboardResponseSchema>;

/** Query string for `GET /v1/cede-arena/leaderboard/position`. */
export const GetArenaGeneralLeaderboardPositionQuerySchema = z.object({
  hubUserId: z.coerce.number().int().positive(),
  scope: ArenaGeneralLeaderboardScopeSchema.optional().default("ranked"),
  window: ArenaGeneralLeaderboardWindowSchema.optional().default("all"),
  sort: ArenaGeneralLeaderboardSortSchema.optional().default("wins"),
});
export type GetArenaGeneralLeaderboardPositionQueryDto = z.infer<typeof GetArenaGeneralLeaderboardPositionQuerySchema>;

/** Response for `GET /v1/cede-arena/leaderboard/position`. `null` when the user is not on the board (scope/window/sort). */
export const GetArenaGeneralLeaderboardPositionResponseSchema = z.object({
  entry: ArenaGeneralLeaderboardEntrySchema.nullable(),
});
export type GetArenaGeneralLeaderboardPositionResponse = z.infer<
  typeof GetArenaGeneralLeaderboardPositionResponseSchema
>;

/** Query string for `GET /v1/cede-arena/leaderboard/stats`. */
export const GetArenaGeneralLeaderboardStatsQuerySchema = z.object({
  scope: ArenaGeneralLeaderboardScopeSchema.optional().default("ranked"),
  window: ArenaGeneralLeaderboardWindowSchema.optional().default("all"),
  /** Which board `totalTraders` counts: `rewards` counts winners only. */
  sort: ArenaGeneralLeaderboardSortSchema.optional().default("wins"),
});
export type GetArenaGeneralLeaderboardStatsQueryDto = z.infer<typeof GetArenaGeneralLeaderboardStatsQuerySchema>;

/** Response for `GET /v1/cede-arena/leaderboard/stats` (hero strip). */
export const GetArenaGeneralLeaderboardStatsResponseSchema = z.object({
  totalRewardsPaidUsdt: NonNegativeDecimalStringSchema,
  totalTraders: z.number().int().nonnegative(),
});
export type GetArenaGeneralLeaderboardStatsResponse = z.infer<typeof GetArenaGeneralLeaderboardStatsResponseSchema>;

/** One credited Arena win, newest first — the post-battle social-proof rail. */
export const ArenaRecentWinSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  /** Credited payout, net of rake — the same COMPETITION_REWARD the leaderboard sums. */
  amountUsdt: NonNegativeDecimalStringSchema,
  /** When the reward was credited. */
  wonAt: z.string().datetime(),
});
export type ArenaRecentWin = z.infer<typeof ArenaRecentWinSchema>;

/** Query string for `GET /v1/cede-arena/recent-wins`. */
export const GetArenaRecentWinsQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(20).optional().default(5),
  /** Look-back window in hours. */
  hours: z.coerce.number().int().positive().max(168).optional().default(24),
  /**
   * Duel campaign whose payouts to omit — the viewer's own just-settled battle,
   * which would otherwise head the rail they are being shown as OTHER players.
   */
  excludeCampaign: z.string().min(1).optional(),
});
export type GetArenaRecentWinsQueryDto = z.infer<typeof GetArenaRecentWinsQuerySchema>;

/**
 * Response for `GET /v1/cede-arena/recent-wins`. `totalPaidUsdt` covers the SAME
 * window and exclusion as `wins`, but every payout in it — not just the returned
 * page — so the rail's header can state the period total.
 */
export const GetArenaRecentWinsResponseSchema = z.object({
  wins: ArenaRecentWinSchema.array(),
  totalPaidUsdt: NonNegativeDecimalStringSchema,
});
export type GetArenaRecentWinsResponse = z.infer<typeof GetArenaRecentWinsResponseSchema>;

/** The free weekly board's buy-in on the wire. `0` is a real board, not "unknown". */
export const ARENA_WEEKLY_FREE_BOARD_BUY_IN = "0";

/**
 * The cross-tier board: one row per player for their WHOLE week, every stake pooled.
 * Reserved key, and the only non-numeric one — a player's mixed standing is not a
 * buy-in, so it can never collide with a rung ops enables.
 */
export const ARENA_WEEKLY_MIXED_BOARD = "mixed";

/**
 * Which board a weekly row or crown belongs to: a buy-in amount, or `mixed`.
 *
 * Carried in the `buyInUsdt` field throughout, because every weekly surface treats
 * that field as the board's identity rather than as money — it keys the colour ramp,
 * the `?stake=` param and the crowns map. Only code that does arithmetic on it (the
 * ramp's `Decimal` comparison) has to branch.
 */
export const ArenaWeeklyBoardSchema = z.union([z.literal(ARENA_WEEKLY_MIXED_BOARD), NonNegativeDecimalStringSchema]);
export type ArenaWeeklyBoard = z.infer<typeof ArenaWeeklyBoardSchema>;

/**
 * How many closed weeks are browsable behind the live one. Bounded so a crafted
 * `?week=9999` can't ask the DB to scan an arbitrarily old window, and because a
 * board older than a quarter has no audience.
 */
export const ARENA_WEEKLY_MAX_WEEK_OFFSET = 12;

/**
 * Weeks back from the live board: `0` is this week, `1` the week that just closed.
 * An offset, not a date — the server owns the Monday-UTC boundary, and a client
 * computing its own would drift from it the moment either side changed timezone rules.
 */
export const ArenaWeekOffsetSchema = z.coerce
  .number()
  .int()
  .min(0)
  .max(ARENA_WEEKLY_MAX_WEEK_OFFSET)
  .optional()
  .default(0);

/**
 * One row of a weekly board. Same shape as the all-time entry plus the board it
 * belongs to — a user who plays several buy-ins ranks independently on each, and
 * once more on `mixed` for the week as a whole.
 */
export const ArenaWeeklyLeaderboardEntrySchema = ArenaGeneralLeaderboardEntrySchema.extend({
  buyInUsdt: ArenaWeeklyBoardSchema,
});
export type ArenaWeeklyLeaderboardEntry = z.infer<typeof ArenaWeeklyLeaderboardEntrySchema>;

/** A live top-3 standing on one weekly board — what the UI renders as a laurel crown. */
export const ArenaWeeklyCrownSchema = z.object({
  hubUserId: z.number().int().positive(),
  /** Carried so the stake ladder can name each board's current holder without a second request. */
  nickname: z.string().nullable(),
  buyInUsdt: ArenaWeeklyBoardSchema,
  rank: z.union([z.literal(1), z.literal(2), z.literal(3)]),
});
export type ArenaWeeklyCrown = z.infer<typeof ArenaWeeklyCrownSchema>;

/** Half-open `[weekStart, weekEnd)` the response was computed over, echoed so clients count down from server truth. */
export const ArenaWeeklyWindowSchema = z.object({
  weekStart: z.iso.datetime(),
  weekEnd: z.iso.datetime(),
});

/** Query string for `GET /v1/cede-arena/weekly-leaderboard`. Page is 0-based. */
export const GetArenaWeeklyLeaderboardQuerySchema = z.object({
  buyInUsdt: ArenaWeeklyBoardSchema,
  weekOffset: ArenaWeekOffsetSchema,
  // Reject an explicit blank `?page=` (which coerces to 0) — only an omitted param defaults.
  page: z
    .preprocess((v) => (v === "" ? NaN : v), z.coerce.number().int().nonnegative().max(ARENA_LEADERBOARD_MAX_PAGE))
    .optional()
    .default(0),
  size: z.coerce.number().int().positive().max(100).optional().default(25),
});
export type GetArenaWeeklyLeaderboardQueryDto = z.infer<typeof GetArenaWeeklyLeaderboardQuerySchema>;

/** Response payload for `GET /v1/cede-arena/weekly-leaderboard`. */
export const GetArenaWeeklyLeaderboardResponseSchema = ArenaWeeklyWindowSchema.extend({
  entries: ArenaWeeklyLeaderboardEntrySchema.array(),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type GetArenaWeeklyLeaderboardResponse = z.infer<typeof GetArenaWeeklyLeaderboardResponseSchema>;

/** Query string for `GET /v1/cede-arena/weekly-leaderboard/position`. */
export const GetArenaWeeklyLeaderboardPositionQuerySchema = z.object({
  hubUserId: z.coerce.number().int().positive(),
  buyInUsdt: ArenaWeeklyBoardSchema,
  weekOffset: ArenaWeekOffsetSchema,
});
export type GetArenaWeeklyLeaderboardPositionQueryDto = z.infer<typeof GetArenaWeeklyLeaderboardPositionQuerySchema>;

/** Response for `GET /v1/cede-arena/weekly-leaderboard/position`. `null` when the user hasn't played this board this week. */
export const GetArenaWeeklyLeaderboardPositionResponseSchema = ArenaWeeklyWindowSchema.extend({
  entry: ArenaWeeklyLeaderboardEntrySchema.nullable(),
});
export type GetArenaWeeklyLeaderboardPositionResponse = z.infer<typeof GetArenaWeeklyLeaderboardPositionResponseSchema>;

/** Query string for `GET /v1/cede-arena/weekly-leaderboard/positions`. */
export const GetArenaWeeklyLeaderboardPositionsQuerySchema = z.object({
  hubUserId: z.coerce.number().int().positive(),
  weekOffset: ArenaWeekOffsetSchema,
});
export type GetArenaWeeklyLeaderboardPositionsQueryDto = z.infer<typeof GetArenaWeeklyLeaderboardPositionsQuerySchema>;

/**
 * Response for `GET /v1/cede-arena/weekly-leaderboard/positions` — one user's standing on
 * EVERY board they played this week, one entry per board (`buyInUsdt` identifies it).
 *
 * A board the user has not played is absent rather than present with a null rank: the
 * board picker draws a dash from the gap, and inventing a rank for an unplayed board
 * would rank someone who never sat down.
 */
export const GetArenaWeeklyLeaderboardPositionsResponseSchema = ArenaWeeklyWindowSchema.extend({
  entries: ArenaWeeklyLeaderboardEntrySchema.array(),
});
export type GetArenaWeeklyLeaderboardPositionsResponse = z.infer<
  typeof GetArenaWeeklyLeaderboardPositionsResponseSchema
>;

/** Query string for `GET /v1/cede-arena/weekly-leaderboard/crowns`. */
export const GetArenaWeeklyCrownsQuerySchema = z.object({
  weekOffset: ArenaWeekOffsetSchema,
});
export type GetArenaWeeklyCrownsQueryDto = z.infer<typeof GetArenaWeeklyCrownsQuerySchema>;

/**
 * Response for `GET /v1/cede-arena/weekly-leaderboard/crowns` — every top-3 holder on
 * every board for the requested week (≤ 3 × board count). Defaults to the live week,
 * which is the one shared request that backs every crown in the app.
 */
export const GetArenaWeeklyCrownsResponseSchema = ArenaWeeklyWindowSchema.extend({
  crowns: ArenaWeeklyCrownSchema.array(),
});
export type GetArenaWeeklyCrownsResponse = z.infer<typeof GetArenaWeeklyCrownsResponseSchema>;

/** The two Arena entry types we ship at v1. Mirrors the Prisma enum. */
export const ArenaEntryTypeSchema = z.enum(["ONE_V_ONE"]);
export type ArenaEntryTypeDto = z.infer<typeof ArenaEntryTypeSchema>;

export { ArenaEntryTierSchema, type ArenaEntryTierDto } from "./entry-tier";

/**
 * Tiers a user may pick themselves (opt-in body, FE stake picker). ONBOARDING is
 * retired as an entry path and rejected at this boundary.
 */
export const UserSelectableArenaTierSchema = ArenaEntryTierSchema.exclude(["ONBOARDING"]);
export type UserSelectableArenaTierDto = z.infer<typeof UserSelectableArenaTierSchema>;

/**
 * One CMS-configured buy-in a user can pick on a paid tier (PAID / SPIN_AND_GO).
 * Array position is the display order — no explicit sort key on the wire.
 * `canberraiEligible: false` makes the amount human-only: the CanberrAI fallback
 * never fills a queue entry at it (anti-farming guard on high stakes). This flag
 * is CMS-only — the public tiers endpoint must never carry it (bot tell).
 */
const ARENA_BUY_IN_AMOUNT_REGEX = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/;

export const ArenaBuyInOptionDtoSchema = z.object({
  /** Buy-in in decimal USDT, up to 2 dp, > 0 — e.g. "0.1", "5", "100". */
  amountUsdt: z
    .string()
    .regex(ARENA_BUY_IN_AMOUNT_REGEX, { error: 'must be a USDT amount with up to 2 decimals, e.g. "0.5"' })
    .refine((value) => !ARENA_BUY_IN_AMOUNT_REGEX.test(value) || new Decimal(value).gt(0), {
      error: "must be greater than 0",
    }),
  /** Disabled amounts stay configured (flags preserved) but are hidden from users and rejected at opt-in. */
  enabled: z.boolean(),
  canberraiEligible: z.boolean(),
  /**
   * Priority tickets granted at finalisation per eligible duelist of a duel
   * played at this buy-in — the winner only, or both entrants, per
   * `cede_arena:api:priority_tickets_all_participants_enable`; a bot and a
   * waived (whitelisted) entry earn nothing either way. Required, not
   * defaulted: a client that omits it must be rejected rather than silently
   * resetting a configured award to 0.
   */
  autopilotPriorityTickets: z.number().int().min(0).max(100),
  /**
   * Bot-fallback cutoff draw bounds for entries queued at THIS amount — a thin
   * stake can reach the bot sooner than a liquid one. Same required-not-defaulted
   * reasoning as the ticket award. Cross-field checks live on the update body.
   */
  botCutoffMinMs: z.number().int().positive().max(MAX_INT4),
  botCutoffMaxMs: z.number().int().positive().max(MAX_INT4),
  /** Wait figure shown in the queue UI at this amount. Display only — never an eligibility input. */
  displayedEstimatedWaitMs: z.number().int().positive().max(MAX_INT4),
  /**
   * How long an entry queued at THIS amount waits before it may pair down to the
   * nearest lower amount of its tier. `0` = eligible immediately. Unrelated to the
   * bot-cutoff bounds: either ordering is meaningful, so no cross-field rule.
   */
  downgradeCutoffMs: z.number().int().nonnegative().max(MAX_INT4),
});
export type ArenaBuyInOptionDto = z.infer<typeof ArenaBuyInOptionDtoSchema>;

/**
 * Default buy-in ladder for paid tiers — single-sourced for the DB seed
 * migration and the CMS "reset to defaults" button.
 * Bots are eligible up to $10; $50+$100 are human-only (a skilled trader
 * reliably beating the house bot must not be able to farm it at high stakes).
 */
export const ARENA_DEFAULT_BUY_IN_TIMING = {
  botCutoffMinMs: 60_000,
  botCutoffMaxMs: 180_000,
  displayedEstimatedWaitMs: 120_000,
  downgradeCutoffMs: 90_000,
} as const;

export const ARENA_DEFAULT_TIER_BUY_INS: ArenaBuyInOptionDto[] = [
  {
    amountUsdt: "0.1",
    enabled: true,
    canberraiEligible: true,
    autopilotPriorityTickets: 0,
    ...ARENA_DEFAULT_BUY_IN_TIMING,
  },
  {
    amountUsdt: "0.5",
    enabled: true,
    canberraiEligible: true,
    autopilotPriorityTickets: 0,
    ...ARENA_DEFAULT_BUY_IN_TIMING,
  },
  {
    amountUsdt: "1",
    enabled: true,
    canberraiEligible: true,
    autopilotPriorityTickets: 0,
    ...ARENA_DEFAULT_BUY_IN_TIMING,
  },
  {
    amountUsdt: "5",
    enabled: true,
    canberraiEligible: true,
    autopilotPriorityTickets: 0,
    ...ARENA_DEFAULT_BUY_IN_TIMING,
  },
  {
    amountUsdt: "10",
    enabled: true,
    canberraiEligible: true,
    autopilotPriorityTickets: 0,
    ...ARENA_DEFAULT_BUY_IN_TIMING,
  },
  {
    amountUsdt: "50",
    enabled: true,
    canberraiEligible: false,
    autopilotPriorityTickets: 0,
    ...ARENA_DEFAULT_BUY_IN_TIMING,
  },
  {
    amountUsdt: "100",
    enabled: true,
    canberraiEligible: false,
    autopilotPriorityTickets: 0,
    ...ARENA_DEFAULT_BUY_IN_TIMING,
  },
];

/**
 * ONBOARDING's range is a first-time user's free-battle bot cutoff:
 * `botCutoffMaxMs < 10s`. A queue-shaped 180s would turn a ~5s search into minutes.
 */
export const ARENA_ONBOARDING_MAX_CUTOFF_MS = 10_000;

/** Cap on ONBOARDING's `durationMs`: the first-time practice duel should take minutes, not a full battle. */
export const ARENA_PRACTICE_MAX_DURATION_MS = 240_000;

// ───────────────────── Cede Arena matchmaking tier config ─────────────────────
//
// Per-tier matchmaking duel settings, edited from the "Cede Arena Matchmaking"
// tab of internal-cms and read by the backend duel-creation + opt-in paths.
// Backed by the `arena_matchmaking_configs` table (one row per tier).

/** Hot pairs the market selector flags when the duel's tier sets no `hotPairsCount`, or the battle has no tier. */
export const ARENA_DEFAULT_HOT_PAIRS_COUNT = 3;

/**
 * One tier's full matchmaking duel config (CMS view). `minVolumeUsd` crosses the
 * wire as a decimal string (Prisma Decimal). Paid tiers carry a `buyIns` ladder
 * the user picks from at opt-in. `pairSymbols` are HL perp symbols; the bot
 * trades only the first.
 */
export const ArenaMatchmakingTierConfigDtoSchema = z.object({
  tier: ArenaEntryTierSchema,
  durationMs: z.number().int().positive(),
  /**
   * @deprecated Legacy single buy-in in whole USDT — now only the fallback charged
   * to opt-ins that don't send a chosen amount (hub-v2, pre-buy-in-ladder clients).
   * Pick from `buyIns` instead. Drop once amount-less opt-ins are gone.
   */
  entryFeeUsdt: z.number().int().nonnegative(),
  /**
   * Buy-in ladder for paid tiers, in display order. Empty on FREE/ONBOARDING.
   * Defaulted so a pre-ladder backend payload keeps parsing during deploys.
   */
  buyIns: z.array(ArenaBuyInOptionDtoSchema).default([]),
  // Unified `BASE/QUOTE[:SETTLE]` pair symbols (e.g. "BTC/USDC:USDC"). The slash
  // is required so the value satisfies the campaign DTO's `${string}/${string}` type.
  pairSymbols: z
    .array(
      z.string().regex(/^[^/]+\/[^/]+(?::.+)?$/, {
        error: 'must be a BASE/QUOTE pair symbol, e.g. "BTC/USDC:USDC"',
      }),
    )
    .min(1),
  /** How many of the duel's pairs the market selector highlights in gold, ranked by paper-trading pair volatility. 0 = none; null = `ARENA_DEFAULT_HOT_PAIRS_COUNT`. */
  hotPairsCount: z.number().int().nonnegative().max(ARENA_MAX_PAIR_SYMBOLS).nullable(),
  minVolumeUsd: NonNegativeDecimalStringSchema,
  initialDeposit: z.number().int().positive(),
  /** Paper maker/taker fee override in bps. Omitted/null = the paper-trading default, `PAPER_MAKER_FEE_BPS` / `PAPER_TAKER_FEE_BPS`. */
  makerFeeBps: z.number().int().nonnegative().max(10000).nullish(),
  takerFeeBps: z.number().int().nonnegative().max(10000).nullish(),
  /**
   * Platform rake in basis points (0–5000, 0.25% steps) taken off each duel's
   * prize pool before the winner is paid. Snapshotted onto every duel campaign
   * created for this tier. The FREE tier's pool is always 0, so its rake is
   * inert — but the field is required here so the CMS form is uniform.
   */
  rakeBps: ArenaRakeBpsSchema,
  /** Optional drawdown-elimination policy applied to every duel created for this tier. Omitted/null ⇒ off. */
  drawdown: FixedDrawdownEliminationConfigSchema.nullish(),
  /**
   * Lower bound of the per-entry bot-fallback cutoff draw, for FREE and — as a
   * first-time user's free-battle cutoff — ONBOARDING. Paid tiers draw from the buy-in's own
   * bounds, and reach for these only when an entry's amount left the ladder.
   */
  botCutoffMinMs: z.number().int().positive().max(MAX_INT4),
  /** Upper bound of the per-entry bot-fallback cutoff draw. Same per-tier vs per-buy-in split as `botCutoffMinMs`. */
  botCutoffMaxMs: z.number().int().positive().max(MAX_INT4),
  /** Static wait figure shown in the queue UI. Display only — never an eligibility input. Same split as `botCutoffMinMs`. */
  displayedEstimatedWaitMs: z.number().int().positive().max(MAX_INT4),
  /**
   * Wait before an entry may pair down to the nearest lower amount of its tier.
   * `0` = eligible immediately. Same per-tier vs per-buy-in split as `botCutoffMinMs`;
   * inert on FREE/ONBOARDING, which have no ladder to step down.
   */
  downgradeCutoffMs: z.number().int().nonnegative().max(MAX_INT4),
  /**
   * Spin & Go table: wait before each bot after the first, drawn per seat in
   * [min, max] from the previous seat fill. Inert on every other tier.
   */
  nextBotDelayMinMs: z.number().int().positive().max(MAX_INT4),
  nextBotDelayMaxMs: z.number().int().positive().max(MAX_INT4),
});
export type ArenaMatchmakingTierConfigDto = z.infer<typeof ArenaMatchmakingTierConfigDtoSchema>;

/** Seats of a Spin & Go table (1v1v1). Bots count as filled seats, never told apart. */
export const ARENA_SPIN_AND_GO_TABLE_SEATS = SPIN_AND_GO_CATEGORY_SEATS.QUICK_MATCH_1V1V1;

/** Bots a Spin & Go buy-in's cohort needs to fill a lone human's table; a bot is busy at one table at a time. */
export const ARENA_SPIN_AND_GO_MIN_BOT_COHORT = ARENA_SPIN_AND_GO_TABLE_SEATS - 1;

/**
 * CMS read projection. The default is read-only rollout compatibility: a new
 * Solo build can read an old API, while the write schema below still requires
 * every row to send an explicit value. Per-buy-in timing gets NO such default —
 * a substituted value would render as a real setting and overwrite the tier's
 * configured one on the next save; a parse error until the API ships is honest.
 */
export const ArenaMatchmakingTierConfigReadDtoSchema = ArenaMatchmakingTierConfigDtoSchema.omit({
  buyIns: true,
}).extend({
  buyIns: z
    .array(
      ArenaBuyInOptionDtoSchema.omit({ autopilotPriorityTickets: true }).extend({
        autopilotPriorityTickets: z.number().int().min(0).max(100).default(0),
        /** CanberrAI bots attributed to this amount (read-only here — written via the bot-attributions endpoint). */
        botHubUserIds: z.array(z.number().int().positive()).default([]),
      }),
    )
    .default([]),
  /** Zero-fee bot cohort (FREE + ONBOARDING; paid tiers attribute per buy-in). Read-only here. */
  botHubUserIds: z.array(z.number().int().positive()).default([]),
  /** Absent only from an API predating the field: read as null, the default. */
  hotPairsCount: ArenaMatchmakingTierConfigDtoSchema.shape.hotPairsCount.default(null),
  /** Null only from an API predating the fields: the form shows them empty and the write schema forces a value. */
  nextBotDelayMinMs: ArenaMatchmakingTierConfigDtoSchema.shape.nextBotDelayMinMs.nullable().default(null),
  nextBotDelayMaxMs: ArenaMatchmakingTierConfigDtoSchema.shape.nextBotDelayMaxMs.nullable().default(null),
});
export type ArenaMatchmakingTierConfigReadDto = z.infer<typeof ArenaMatchmakingTierConfigReadDtoSchema>;

/** Response payload for `GET /v1/cms/arena-matchmaking-config` — all tiers. */
export const GetArenaMatchmakingConfigsResponseSchema = z.object({
  configs: z.array(ArenaMatchmakingTierConfigReadDtoSchema),
});
export type GetArenaMatchmakingConfigsResponse = z.infer<typeof GetArenaMatchmakingConfigsResponseSchema>;

/**
 * True for the tiers that charge a buy-in and carry a `buyIns` ladder (PAID,
 * SPIN_AND_GO) — the tiers a free-entry ticket can fund.
 */
/** Type predicate so a paid-only surface (buy-in ladders, re-engagement) narrows without a cast. */
export const isPaidArenaTier = (tier: ArenaEntryTierDto): tier is ArenaPaidTierDto =>
  tier === "PAID" || tier === "SPIN_AND_GO";

/** Canonical form of a USDT amount string so "0.10" and "0.1" collide in uniqueness checks. */
const normalizeUsdtAmount = (amountUsdt: string) => new Decimal(amountUsdt).toString();

/**
 * Body for `PUT /v1/cms/arena-matchmaking-config` — updates a single existing tier
 * row; a missing row is an error, not an insert. FREE must stay free. Neither
 * FREE nor ONBOARDING carries a buy-in ladder, since neither is user-picked. Paid tiers must keep at least one
 * enabled buy-in so the tier can't be accidentally left unenterable.
 */
export const UpdateArenaMatchmakingConfigBodySchema = ArenaMatchmakingTierConfigDtoSchema.refine(
  (config) => config.tier !== "FREE" || config.entryFeeUsdt === 0,
  { error: "the FREE tier must have a 0 entry fee", path: ["entryFeeUsdt"] },
)
  .refine((config) => isPaidArenaTier(config.tier) || config.buyIns.length === 0, {
    error: "only the paid tiers (PAID, SPIN_AND_GO) can have a buy-in ladder",
    path: ["buyIns"],
  })
  .refine((config) => !isPaidArenaTier(config.tier) || config.buyIns.some((buyIn) => buyIn.enabled), {
    error: "paid tiers need at least one enabled buy-in",
    path: ["buyIns"],
  })
  .refine(
    (config) => {
      if (config.buyIns.some((buyIn) => !ARENA_BUY_IN_AMOUNT_REGEX.test(buyIn.amountUsdt))) return true;
      return new Set(config.buyIns.map((buyIn) => normalizeUsdtAmount(buyIn.amountUsdt))).size === config.buyIns.length;
    },
    { error: "buy-in amounts must be unique", path: ["buyIns"] },
  )
  .refine((config) => config.nextBotDelayMinMs <= config.nextBotDelayMaxMs, {
    error: "next bot delay min cannot exceed max",
    path: ["nextBotDelayMaxMs"],
  })
  .refine((config) => config.hotPairsCount == null || config.hotPairsCount <= config.pairSymbols.length, {
    error: "hot pairs count cannot exceed the number of pair symbols",
    path: ["hotPairsCount"],
  })
  .refine((config) => config.pairSymbols.length <= ARENA_MAX_PAIR_SYMBOLS, {
    // A duel campaign copies the tier's pairSymbols verbatim; the campaign entity caps
    // them at ARENA_MAX_PAIR_SYMBOLS. Enforce it here too so an over-cap tier is rejected
    // at write time instead of stranding every duel later.
    error: `at most ${ARENA_MAX_PAIR_SYMBOLS} pair symbols are allowed`,
    path: ["pairSymbols"],
  })
  .refine((config) => new Set(config.pairSymbols).size === config.pairSymbols.length, {
    // A duplicate doubles that duel's per-pair PnL state and trade-reporting fan-out
    // while adding no market.
    error: "pair symbols must be unique",
    path: ["pairSymbols"],
  })
  .refine((body) => body.botCutoffMinMs <= body.botCutoffMaxMs, {
    error: "Minimum cutoff must not exceed the maximum",
    path: ["botCutoffMinMs"],
  })
  .refine((body) => body.displayedEstimatedWaitMs <= body.botCutoffMaxMs, {
    error: "Displayed wait must not exceed the maximum cutoff",
    path: ["displayedEstimatedWaitMs"],
  })
  .refine((body) => body.tier !== "ONBOARDING" || body.botCutoffMaxMs < ARENA_ONBOARDING_MAX_CUTOFF_MS, {
    error: `Onboarding's maximum must stay under ${ARENA_ONBOARDING_MAX_CUTOFF_MS / 1000}s (it is the bot cutoff of a first-time user's free battle)`,
    path: ["botCutoffMaxMs"],
  })
  .refine((body) => body.tier !== "ONBOARDING" || body.durationMs <= ARENA_PRACTICE_MAX_DURATION_MS, {
    error: `Onboarding's duration must be at most ${ARENA_PRACTICE_MAX_DURATION_MS / 1000}s (the first-time practice duel should stay short)`,
    path: ["durationMs"],
  })
  .superRefine((config, ctx) => {
    // Same bounds the tier block enforces, per ladder row. Amounts are free to
    // order their cutoffs however liquidity demands — no cross-row rule.
    config.buyIns.forEach((buyIn, index) => {
      if (buyIn.botCutoffMinMs > buyIn.botCutoffMaxMs) {
        ctx.addIssue({
          code: "custom",
          path: ["buyIns", index, "botCutoffMinMs"],
          message: `${buyIn.amountUsdt} USDT: minimum cutoff must not exceed the maximum`,
        });
      }
      if (buyIn.displayedEstimatedWaitMs > buyIn.botCutoffMaxMs) {
        ctx.addIssue({
          code: "custom",
          path: ["buyIns", index, "displayedEstimatedWaitMs"],
          message: `${buyIn.amountUsdt} USDT: displayed wait must not exceed the maximum cutoff`,
        });
      }
    });
  })
  .superRefine((config, ctx) => {
    // Ticket value must rise with the stake, or the cheap rung reopens the farm.
    // Sort numerically: array position is display order, not a pricing order.
    const ascending = config.buyIns
      .filter((buyIn) => ARENA_BUY_IN_AMOUNT_REGEX.test(buyIn.amountUsdt))
      .sort((a, b) => new Decimal(a.amountUsdt).comparedTo(new Decimal(b.amountUsdt)));
    let lower: (typeof ascending)[number] | undefined;
    for (const higher of ascending) {
      if (lower !== undefined && higher.autopilotPriorityTickets < lower.autopilotPriorityTickets) {
        ctx.addIssue({
          code: "custom",
          path: ["buyIns"],
          message:
            `${higher.amountUsdt} USDT awards ${higher.autopilotPriorityTickets} tickets but the smaller ` +
            `${lower.amountUsdt} USDT awards ${lower.autopilotPriorityTickets} — ticket counts must not decrease as the buy-in rises`,
        });
        return;
      }
      lower = higher;
    }
  });
export type UpdateArenaMatchmakingConfigBody = z.infer<typeof UpdateArenaMatchmakingConfigBodySchema>;

/** The tiers that carry a buy-in ladder — the only ones with per-amount bot attribution. */
export const ArenaPaidTierSchema = ArenaEntryTierSchema.extract(["PAID", "SPIN_AND_GO"]);
export type ArenaPaidTierDto = z.infer<typeof ArenaPaidTierSchema>;

/**
 * Body for `PUT /v1/cms/arena-matchmaking-config/bot-attributions` — the
 * bot↔buy-in attribution (which CanberrAI bots may fill which stake), written
 * separately from the ladder save so the two CMS editors can't clobber each
 * other. DELTA semantics: every listed (tier, amount) must be an existing
 * ladder row (a missing row is an error, not an upsert); ladder rows not
 * listed keep their current cohort, and an OMITTED `freeTierBotHubUserIds`
 * leaves the FREE cohort unchanged (present = replace it) — so a client sends
 * only the cohorts it actually changed and concurrent editors can't revert
 * each other's untouched stakes.
 */
/**
 * A Spin & Go cohort with bots but too few to fill a lone human's table: its one
 * bot takes a seat and the next is never free. Empty stays valid (no bot fill).
 */
export const isUnfillableSpinAndGoCohort = (buyIn: { tier: string; botHubUserIds: number[] }): boolean =>
  buyIn.tier === "SPIN_AND_GO" &&
  buyIn.botHubUserIds.length > 0 &&
  buyIn.botHubUserIds.length < ARENA_SPIN_AND_GO_MIN_BOT_COHORT;

export const unfillableSpinAndGoCohortMessage = (amountUsdt: string) =>
  `S&G ${amountUsdt} USDT needs at least ${ARENA_SPIN_AND_GO_MIN_BOT_COHORT} bots (or none) to fill a table`;

export const UpdateArenaBotAttributionsBodySchema = z
  .object({
    buyIns: z.array(
      z.object({
        tier: ArenaPaidTierSchema,
        amountUsdt: z
          .string()
          .regex(ARENA_BUY_IN_AMOUNT_REGEX, { error: 'must be a USDT amount with up to 2 decimals, e.g. "0.5"' }),
        botHubUserIds: z.array(z.number().int().positive()),
      }),
    ),
    freeTierBotHubUserIds: z.array(z.number().int().positive()).optional(),
    /** Replaces the ONBOARDING tier's cohort; omitted = unchanged. Empty = unconfigured, so the guided duel picks from the whole pool. */
    onboardingTierBotHubUserIds: z.array(z.number().int().positive()).optional(),
  })
  .superRefine((body, ctx) => {
    const seen = new Set<string>();
    body.buyIns.forEach((buyIn, index) => {
      const key = `${buyIn.tier}:${normalizeUsdtAmount(buyIn.amountUsdt)}`;
      if (seen.has(key)) {
        ctx.addIssue({
          code: "custom",
          path: ["buyIns", index],
          message: `${buyIn.tier} ${buyIn.amountUsdt} USDT is listed twice`,
        });
      }
      seen.add(key);
      if (new Set(buyIn.botHubUserIds).size !== buyIn.botHubUserIds.length) {
        ctx.addIssue({
          code: "custom",
          path: ["buyIns", index, "botHubUserIds"],
          message: `${buyIn.tier} ${buyIn.amountUsdt} USDT lists a bot twice`,
        });
      }
      if (isUnfillableSpinAndGoCohort(buyIn)) {
        ctx.addIssue({
          code: "custom",
          path: ["buyIns", index, "botHubUserIds"],
          message: unfillableSpinAndGoCohortMessage(buyIn.amountUsdt),
        });
      }
    });
    if (
      body.freeTierBotHubUserIds !== undefined &&
      new Set(body.freeTierBotHubUserIds).size !== body.freeTierBotHubUserIds.length
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["freeTierBotHubUserIds"],
        message: "the free-tier cohort lists a bot twice",
      });
    }
    if (
      body.onboardingTierBotHubUserIds !== undefined &&
      new Set(body.onboardingTierBotHubUserIds).size !== body.onboardingTierBotHubUserIds.length
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["onboardingTierBotHubUserIds"],
        message: "the onboarding cohort lists a bot twice",
      });
    }
  });
export type UpdateArenaBotAttributionsBody = z.infer<typeof UpdateArenaBotAttributionsBodySchema>;

/**
 * Public per-tier matchmaking config, served by `GET /v1/cede-arena/matchmaking-tiers`.
 * Deliberately NOT the CMS shape: `buyIns` is stripped to enabled amounts only —
 * `canberraiEligible` must never reach a public payload (a network tab would
 * reveal which stakes the bot fills, breaking bot indistinguishability). The
 * bot-cutoff bounds and next-bot delays are withheld for the same reason, and `displayedEstimatedWaitMs`
 * with them: the me-entry payload is the single source for the shown wait, read
 * through the validating tier reader, so a second raw copy can't disagree with it.
 */
export const ArenaMatchmakingTierPublicConfigDtoSchema = ArenaMatchmakingTierConfigDtoSchema.omit({
  buyIns: true,
  botCutoffMinMs: true,
  botCutoffMaxMs: true,
  displayedEstimatedWaitMs: true,
  downgradeCutoffMs: true,
  nextBotDelayMinMs: true,
  nextBotDelayMaxMs: true,
}).extend({
  /**
   * Enabled buy-ins only, in display order, with the ticket award so Hub can
   * show the earning ladder. Never carries bot-eligibility. The ticket count is
   * defaulted here (unlike the CMS write body) so a new client still parses an
   * older API's response during the deploy window.
   */
  buyIns: z
    .array(
      ArenaBuyInOptionDtoSchema.pick({ amountUsdt: true }).extend({
        autopilotPriorityTickets: z.number().int().min(0).max(100).default(0),
      }),
    )
    .default([]),
  /** Absent only from an API predating the field: read as null, the default. */
  hotPairsCount: ArenaMatchmakingTierConfigDtoSchema.shape.hotPairsCount.default(null),
});
export type ArenaMatchmakingTierPublicConfigDto = z.infer<typeof ArenaMatchmakingTierPublicConfigDtoSchema>;

export const GetArenaMatchmakingTiersResponseSchema = z.object({
  tiers: z.array(ArenaMatchmakingTierPublicConfigDtoSchema),
});
export type GetArenaMatchmakingTiersResponse = z.infer<typeof GetArenaMatchmakingTiersResponseSchema>;

/**
 * Response payload for `GET /v1/cede-arena/discord-status`. Drives the
 * pre-click gate on the hub OptInCard: the opt-in button is only
 * rendered when `linked && inGuild`. Otherwise the card surfaces the
 * appropriate CTA (Connect Discord / Join the Arena Discord) using
 * `inviteUrl`.
 */
export const GetArenaDiscordStatusResponseSchema = z.object({
  linked: z.boolean(),
  /** False whenever `linked` is false, OR the linked snowflake isn't a current member of the Cede Arena guild. */
  inGuild: z.boolean(),
  /** Public invite link to the Cede Arena Discord server. */
  inviteUrl: z.url(),
  /** Linked Discord snowflake — null when `linked` is false. Used as analytics property on opt-in click. */
  discordUserId: z.string().nullable(),
  /** Linked Discord username — null when `linked` is false. Used as analytics property on opt-in click. */
  discordUsername: z.string().nullable(),
});
export type GetArenaDiscordStatusResponse = z.infer<typeof GetArenaDiscordStatusResponseSchema>;

/**
 * Response payload for `GET /v1/cede-arena/queue-status`. Drives the
 * compact "N ready · M awaiting payment" line in the Hub OptInCard.
 *
 * Counts are inflated by a CMS-configured virtual delta (see
 * `ArenaQueueDisplaySettingsDto`) so the queue feels active even before
 * organic volume picks up. The virtual delta is sticky across all viewers
 * within a refresh window (default 4h) so numbers don't jitter on each poll.
 */
export const GetArenaQueueStatusResponseSchema = z.object({
  /** Real PAID/PAID_NOTIFIED count + CMS-configured virtual delta. */
  readyToTrade: z.number().int().nonnegative(),
  /** Real PENDING/PENDING_NOTIFIED count + CMS-configured virtual delta. */
  waitingForPayment: z.number().int().nonnegative(),
});
export type GetArenaQueueStatusResponse = z.infer<typeof GetArenaQueueStatusResponseSchema>;

/**
 * Response payload for `GET /v1/cede-arena/geo-eligibility`.
 *
 * Lets the UI render the entry CTA as unavailable before the click instead of
 * surfacing a 403 after it. Advisory only — `ArenaGeoBlockGuard` is the actual
 * control, and re-checks on every entry POST.
 *
 * `countryCode` is null when unresolvable (non-Cloudflare path, `XX`, Tor `T1`).
 */
export const GetArenaGeoEligibilityResponseSchema = z.object({
  /** True when this request's country may not enter competitions. */
  blocked: z.boolean(),
  /** ISO 3166-1 alpha-2 country resolved at the edge, or null if unresolvable. */
  countryCode: z.string().nullable(),
});
export type GetArenaGeoEligibilityResponse = z.infer<typeof GetArenaGeoEligibilityResponseSchema>;

/**
 * CMS-managed knobs for the public queue display. Edited on the
 * `/arena-entries` page of internal-cms.
 *
 * Two independent inclusive integer ranges, one per bucket — a single
 * value is picked per refresh window from each range and added to the
 * matching real count (PAID for ready-to-trade, PENDING for
 * awaiting-payment). `refreshHours` is shared; the two buckets use
 * distinct seed salts so they can land on different numbers within the
 * same window. All viewers inside the same window see the same numbers.
 */
export const ArenaQueueDisplaySettingsDtoSchema = z.object({
  virtualReadyMin: z.number().int().nonnegative(),
  virtualReadyMax: z.number().int().nonnegative(),
  virtualWaitingMin: z.number().int().nonnegative(),
  virtualWaitingMax: z.number().int().nonnegative(),
  refreshHours: z.number().int().positive(),
});
export type ArenaQueueDisplaySettingsDto = z.infer<typeof ArenaQueueDisplaySettingsDtoSchema>;

/** Body for `PUT /v1/cms/arena-queue-display-settings`. */
export const UpdateArenaQueueDisplaySettingsBodySchema = ArenaQueueDisplaySettingsDtoSchema.refine(
  (s) => s.virtualReadyMax >= s.virtualReadyMin,
  { error: "virtualReadyMax must be >= virtualReadyMin", path: ["virtualReadyMax"] },
).refine((s) => s.virtualWaitingMax >= s.virtualWaitingMin, {
  error: "virtualWaitingMax must be >= virtualWaitingMin",
  path: ["virtualWaitingMax"],
});
export type UpdateArenaQueueDisplaySettingsBody = z.infer<typeof UpdateArenaQueueDisplaySettingsBodySchema>;

/** Which end of the regularity distribution a re-engagement ping recruits from. */
export const ArenaReengagementCriteriaSchema = z.enum(["MOST_REGULAR", "LEAST_REGULAR", "MIXED"]);
export type ArenaReengagementCriteriaDto = z.infer<typeof ArenaReengagementCriteriaSchema>;

/**
 * CMS-managed knobs for re-engagement pings: when a user is queued alone at a
 * paid buy-in, past players of that buy-in are invited back. Edited on the
 * `/arena-reengagement` page of internal-cms.
 *
 * There is deliberately NO cutoff knob here. How long the waiting user searches
 * before the CanberrAI fallback stays `botCutoffMinMs`/`botCutoffMaxMs` on the
 * matchmaking tabs — a ping never silently extends somebody's wait.
 */
export const ArenaReengagementSettingsDtoSchema = z.object({
  /** Master switch. Off ⇒ the pairing tick never looks for lonely entries. */
  enabled: z.boolean(),
  /** How many players one lonely entry recruits. */
  recipientsPerPing: z.number().int().positive(),
  criteria: ArenaReengagementCriteriaSchema,
  /** Trailing window the regularity count is measured over. */
  lookbackDays: z.number().int().positive(),
  /** A user pinged within this many hours is skipped, whatever their rank. */
  cooldownHours: z.number().int().positive(),
  /** Minimum queue wait before an entry counts as lonely — lets an imminent pairing happen first. */
  pingGraceMs: z.number().int().nonnegative(),
  /** Rungs DOWN the buy-in ladder to widen into when the exact amount can't fill the quota. 0 ⇒ same buy-in only. */
  ladderFallbackSteps: z.number().int().nonnegative(),
});
export type ArenaReengagementSettingsDto = z.infer<typeof ArenaReengagementSettingsDtoSchema>;

/** Body for `PUT /v1/cms/arena-reengagement-settings`. */
export const UpdateArenaReengagementSettingsBodySchema = ArenaReengagementSettingsDtoSchema;
export type UpdateArenaReengagementSettingsBody = z.infer<typeof UpdateArenaReengagementSettingsBodySchema>;

/**
 * Global CMS policy for user-created private competitions. A room owner sets up
 * their own room (entry fee, repartition) from the Arena UI — but the rake is
 * Cede's platform cut, NOT the owner's to set, so it is a single ops-controlled
 * value here (never a room-create field) and is snapshotted onto each room at
 * creation. Backed by the singleton `arena_private_competition_configs` row.
 * Edited only from the Cede Arena section of internal-cms. Also carries the
 * global default paper maker/taker fee applied to every user-created private
 * Arena competition (omitted/null = keep the paper-trading module default of
 * 1.5/4.5 bps; 0 disables the fee).
 */
export const ArenaPrivateCompetitionSettingsDtoSchema = z.object({
  /** Platform rake (basis points, 0–5000, 0.25% steps) taken off every private-room pool. */
  rakeBps: ArenaRakeBpsSchema,
  makerFeeBps: z.number().int().nonnegative().max(10000).nullish(),
  takerFeeBps: z.number().int().nonnegative().max(10000).nullish(),
  /** Optional drawdown-elimination policy applied to every private room. Omitted/null ⇒ off. */
  drawdown: FixedDrawdownEliminationConfigSchema.nullish(),
});
export type ArenaPrivateCompetitionSettingsDto = z.infer<typeof ArenaPrivateCompetitionSettingsDtoSchema>;

/** Response payload for `GET /v1/cms/arena-private-competition-settings`. */
export const GetArenaPrivateCompetitionSettingsResponseSchema = ArenaPrivateCompetitionSettingsDtoSchema;
export type GetArenaPrivateCompetitionSettingsResponse = z.infer<
  typeof GetArenaPrivateCompetitionSettingsResponseSchema
>;

/** Body for `PUT /v1/cms/arena-private-competition-settings`. */
export const UpdateArenaPrivateCompetitionSettingsBodySchema = ArenaPrivateCompetitionSettingsDtoSchema;
export type UpdateArenaPrivateCompetitionSettingsBody = z.infer<typeof UpdateArenaPrivateCompetitionSettingsBodySchema>;

/**
 * Public read of the platform rake that applies to private competitions, served
 * by `GET /v1/cede-arena/private/rake-policy`. Lets the room-create prize-pool
 * simulator show the platform deduction before the room (and its snapshot) exist.
 * Read-only mirror of the CMS `rakeBps` value — no auth, no other config leaked.
 */
export const GetArenaPrivateCompetitionRakePolicyResponseSchema = z.object({
  /** Platform rake in basis points (0–5000) currently applied to new private rooms. */
  platformRakeBps: ArenaRakeBpsSchema,
});
export type GetArenaPrivateCompetitionRakePolicyResponse = z.infer<
  typeof GetArenaPrivateCompetitionRakePolicyResponseSchema
>;

/**
 * Global Cede Arena platform config (all competition types). Today it holds the
 * house account the platform rake is credited to — an existing HubUser id.
 * `null` ⇒ the platform rake is withheld (uncredited) until ops sets a recipient.
 * Backed by the singleton `arena_platform_configs` row; edited from the Cede
 * Arena section of internal-cms.
 *
 * Deployment order: this schema is shared by `apps/api` and `apps/admin/internal-cms`.
 * When adding a required field (as `teamHubUserIds` was), deploy the API first —
 * the CMS PUT sends the full DTO, so an older API that doesn't know the field
 * would reject a newer CMS's payload.
 */
export const ArenaPlatformSettingsDtoSchema = z.object({
  /** Existing HubUser id that receives the platform rake, or null to withhold it. */
  rakeRecipientHubUserId: z.number().int().positive().nullable(),
  /**
   * Existing HubUser ids that are Cede team accounts. Surfaced in the team's
   * Discord notifications so a team member's manual play is distinguishable from
   * a real user (🧑‍💼 Team) and from a CanberrAI bot (🤖 Bot). Notification-only —
   * never filters leaderboard / win logic. Empty ⇒ no accounts flagged as team.
   */
  teamHubUserIds: z.array(z.number().int().positive()),
});
export type ArenaPlatformSettingsDto = z.infer<typeof ArenaPlatformSettingsDtoSchema>;

/** Response payload for `GET /v1/cms/arena-platform-settings`. */
export const GetArenaPlatformSettingsResponseSchema = ArenaPlatformSettingsDtoSchema;
export type GetArenaPlatformSettingsResponse = z.infer<typeof GetArenaPlatformSettingsResponseSchema>;

/** Body for `PUT /v1/cms/arena-platform-settings`. */
export const UpdateArenaPlatformSettingsBodySchema = ArenaPlatformSettingsDtoSchema;
export type UpdateArenaPlatformSettingsBody = z.infer<typeof UpdateArenaPlatformSettingsBodySchema>;

/**
 * CanberrAI CMS config — the bot pool + auto-match knobs the ops
 * team manages from the Cede Arena section of internal-cms. Backed by the
 * singleton `canberrai_configs` row. `strategyConfig` (the bot's tradeable
 * markets) is intentionally NOT exposed here — it's a separate, lower-
 * frequency tuning surface; this DTO covers only what ops touches to run a
 * local/staging test (which existing HubUsers act as bots and how long an
 * unmatched paid entry waits before auto-match).
 */
/**
 * A resolved bot in the pool — read-only enrichment the GET returns so the CMS
 * can wire the "Match with bot" action (it needs the bot's wallet to inject
 * into `authorizedParticipants`). `wallet` is `null` when the id has no
 * resolvable arena wallet (a misconfigured pool entry the CMS should surface).
 */
export const CanberraiBotDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  wallet: z.string().nullable(),
  /** Free arena virtual balance (decimal-USDT string), or `null` if none exists. */
  freeUsdt: z.string().nullable(),
  /**
   * The bot HubUser's arena nickname — the AI "persona" shown to opponents.
   * `null` when the account has no nickname set; ops should set
   * distinct nicknames so matches don't all read as the same opponent.
   */
  nickname: z.string().nullable(),
});
export type CanberraiBotDto = z.infer<typeof CanberraiBotDtoSchema>;

/** Sanity ceiling on the bot rematch counters: the consecutive cap also bounds a `LIMIT`. */
const MAX_BOT_REMATCH_LOSS_CAP = 1_000;

/** Sanity ceiling on the lifetime bot-duel cap: past this the gate can never bind. */
const MAX_BOT_DUEL_LIFETIME_CAP = 1_000;

/** Sanity ceiling on the bot wait ladder: a handful of steps is the intended shape. */
export const MAX_BOT_WAIT_LADDER_STEPS = 20;

/** Ceiling on the bot wait ladder's rolling window, in days. */
export const MAX_BOT_WAIT_LADDER_WINDOW_DAYS = 365;

/**
 * One step of the bot wait ladder. A user whose paid-duel count is at
 * least `fromPaidDuels` (and below the next step's) waits an extra
 * `[extraMinMs, extraMaxMs]` on top of their buy-in's bot cutoff before a bot steps in.
 */
export const CanberraiBotWaitLadderStepDtoSchema = z.object({
  fromPaidDuels: z.number().int().nonnegative().max(MAX_INT4),
  extraMinMs: z.number().int().nonnegative().max(MAX_INT4),
  extraMaxMs: z.number().int().nonnegative().max(MAX_INT4),
});
export type CanberraiBotWaitLadderStepDto = z.infer<typeof CanberraiBotWaitLadderStepDtoSchema>;

/**
 * Ceiling on the auto-accept ready-check window. The value also delays the duel
 * creation job, and the arena sweep re-drives any proposal still waiting past its
 * 60s recovery grace, so a larger window would make the sweep fight the delay.
 * Well under `MAX_INT4` too, so the INTEGER column can always hold it.
 */
export const MAX_ARENA_AUTO_ACCEPT_READY_CHECK_TIMEOUT_MS = 30_000;

/** Entry pacing bounds, in ms. The floor is one strategy tick (the unpaced behaviour). */
export const MIN_CANBERRAI_ENTRY_INTERVAL_MS = 250;
export const MAX_CANBERRAI_ENTRY_INTERVAL_MS = 60_000;

export const CanberraiConfigDtoSchema = z.object({
  /** Existing `HubUser` ids that may act as opponents. Empty ⇒ feature off. */
  botHubUserIds: z.array(z.number().int().positive()),
  /** Delay before the duel starts after the match, in ms (0 = immediate). */
  duelStartDelayMs: z.number().int().nonnegative(),
  /** Ready-check window in ms: how long each side has to confirm a proposed match. */
  readyCheckTimeoutMs: z.number().int().positive(),
  /**
   * Ready-check window in ms for an auto-accepted match
   * (`cede_arena:api:auto_accept_match_enable`). Nobody confirms, so this only
   * sizes the "match found" beat before the duel takes over. A match where one
   * side was matched at a buy-in it did not pick still uses the full
   * `readyCheckTimeoutMs` — that side has a real decision to make.
   *
   * Optional on the RESPONSE only, for the deploy window (an older API doesn't
   * send it); the update body below re-requires it. Bounded by
   * {@link MAX_ARENA_AUTO_ACCEPT_READY_CHECK_TIMEOUT_MS}.
   */
  autoAcceptReadyCheckTimeoutMs: z
    .number()
    .int()
    .positive()
    .max(MAX_ARENA_AUTO_ACCEPT_READY_CHECK_TIMEOUT_MS)
    .optional(),
  /** Re-queue cooldown in ms applied to the confirmer when the opponent no-shows the ready check. */
  rematchCooldownMs: z.number().int().positive(),
  /** How long after a battle's results finalize a directed rematch may still be proposed, in ms. */
  rematchProposeWindowMs: z.number().int().positive(),
  /**
   * Bot rematch limit — the user's current unbroken run of wins over CanberrAI
   * bots in staked duels. At this many the Rematch CTA is retired until they
   * lose one. Counted across the whole bot pool; human opponents are never
   * limited. `0` = off.
   */
  botRematchMaxConsecutiveLosses: z.number().int().nonnegative().max(MAX_BOT_REMATCH_LOSS_CAP),
  /** Low end of the consecutive range; equal to the max means a fixed threshold. */
  botRematchMinConsecutiveLosses: z.number().int().nonnegative().max(MAX_BOT_REMATCH_LOSS_CAP),
  /**
   * Bot rematch limit — bot losses to this user inside `botRematchLossWindowMs`,
   * order-independent. Unlike the consecutive count it does not reset on a loss,
   * only decays as duels age out of the window. OR'd with the consecutive limit.
   * `0` = off.
   */
  botRematchMaxLossesInWindow: z.number().int().nonnegative().max(MAX_BOT_REMATCH_LOSS_CAP),
  /** Low end of the windowed range; equal to the max means a fixed threshold. */
  botRematchMinLossesInWindow: z.number().int().nonnegative().max(MAX_BOT_REMATCH_LOSS_CAP),
  /** The rolling window `botRematchMaxLossesInWindow` counts over, in ms. */
  botRematchLossWindowMs: z.number().int().positive().max(MAX_INT4),
  /**
   * Lifetime cap on PAID duels a user may play against a CanberrAI bot. Counted
   * at assignment across the whole bot pool; FREE and ONBOARDING duels are
   * outside the count, ticket-funded ones are inside it. Past the cap the user
   * is only ever human-paired. The effective cap is drawn per user from
   * `[min, max]` so it can't be learned by playing. Both ends `0` = off.
   */
  botDuelMaxLifetimeCap: z.number().int().nonnegative().max(MAX_BOT_DUEL_LIFETIME_CAP),
  /** Low end of the lifetime-cap range; equal to the max means a fixed cap. */
  botDuelMinLifetimeCap: z.number().int().nonnegative().max(MAX_BOT_DUEL_LIFETIME_CAP),
  /**
   * Bot wait ladder, ascending by `fromPaidDuels`, first step at 0. The more paid
   * duels (fee > 0, against anyone) a user played inside the window, the longer
   * they wait before a bot. Optional on the RESPONSE only, for the deploy window.
   */
  botWaitLadder: z.array(CanberraiBotWaitLadderStepDtoSchema).optional(),
  /** Days of paid duels the ladder counts; `null` = lifetime. Optional on the RESPONSE only, for the deploy window. */
  botWaitLadderWindowDays: z.number().int().positive().max(MAX_BOT_WAIT_LADDER_WINDOW_DAYS).nullable().optional(),
  /** Queue safety TTL in ms: entries still waiting past this age are auto-ejected with a refund (backstop). */
  queueTtlMs: z.number().int().positive(),
  /**
   * Minimum gap between a bot's OPENING orders, in ms. Exits are never paced.
   * `MIN_CANBERRAI_ENTRY_INTERVAL_MS` = one strategy tick, i.e. unpaced.
   * Optional on the RESPONSE only, for the deploy window.
   */
  entryIntervalMs: z
    .number()
    .int()
    .min(MIN_CANBERRAI_ENTRY_INTERVAL_MS)
    .max(MAX_CANBERRAI_ENTRY_INTERVAL_MS)
    .optional(),
  /**
   * Stand down entirely while ahead of the best opponent by more than this
   * fraction of the starting stack. `0` = off. Optional on the RESPONSE only,
   * for the deploy window.
   */
  leadStandDownFraction: z.number().min(0).max(1).optional(),
  /** Read-only: `botHubUserIds` resolved to their arena wallets + balances. Ignored on write. */
  bots: z.array(CanberraiBotDtoSchema),
  /**
   * Read-only: the max enabled canberrai-eligible buy-in (decimal-USDT string)
   * across paid tiers — the free balance a bot needs to cover any stake it can
   * be matched at. "0" when no amount is bot-eligible.
   */
  buyInUsdt: z.string(),
  /**
   * Read-only: resolved value of the `cede_arena:api:canberrai_auto_match_enable`
   * feature flag. When `false` the backend auto-match cron is a no-op and the CMS
   * locks this section behind an "enable the flag first" prompt. Ignored on write.
   */
  autoMatchEnabled: z.boolean(),
});
export type CanberraiConfigDto = z.infer<typeof CanberraiConfigDtoSchema>;

/**
 * Body for `PUT /v1/cms/canberrai-config`. Only the ops-managed fields are
 * writable — the server-derived enrichments (`bots`, `buyInUsdt`,
 * `autoMatchEnabled`) are omitted.
 */
export const UpdateCanberraiConfigBodySchema = CanberraiConfigDtoSchema.omit({
  bots: true,
  buyInUsdt: true,
  autoMatchEnabled: true,
})
  // Optional on the response for the deploy window, but ops always writes it.
  .extend({
    autoAcceptReadyCheckTimeoutMs: z.number().int().positive().max(MAX_ARENA_AUTO_ACCEPT_READY_CHECK_TIMEOUT_MS),
    botWaitLadder: z.array(CanberraiBotWaitLadderStepDtoSchema).min(1).max(MAX_BOT_WAIT_LADDER_STEPS),
    botWaitLadderWindowDays: z.number().int().positive().max(MAX_BOT_WAIT_LADDER_WINDOW_DAYS).nullable(),
    entryIntervalMs: z.number().int().min(MIN_CANBERRAI_ENTRY_INTERVAL_MS).max(MAX_CANBERRAI_ENTRY_INTERVAL_MS),
    leadStandDownFraction: z.number().min(0).max(1),
  })
  .superRefine((config, ctx) => {
    // Every user needs a step, so the ladder has to start at 0 paid duels.
    if (!config.botWaitLadder.some((step) => step.fromPaidDuels === 0)) {
      ctx.addIssue({
        code: "custom",
        path: ["botWaitLadder"],
        message: "Bot wait ladder: the first step must start at 0 paid duels.",
      });
    }
    if (new Set(config.botWaitLadder.map((step) => step.fromPaidDuels)).size !== config.botWaitLadder.length) {
      ctx.addIssue({
        code: "custom",
        path: ["botWaitLadder"],
        message: "Bot wait ladder: two steps start at the same paid-duel count.",
      });
    }
    config.botWaitLadder.forEach((step, index) => {
      if (step.extraMinMs > step.extraMaxMs) {
        ctx.addIssue({
          code: "custom",
          path: ["botWaitLadder", index, "extraMinMs"],
          message: `Bot wait ladder: the step from ${step.fromPaidDuels} paid duels has min above max.`,
        });
      }
      // Past the TTL the entry is ejected before any bot, which reads as a stalled queue.
      if (step.extraMaxMs >= config.queueTtlMs) {
        ctx.addIssue({
          code: "custom",
          path: ["botWaitLadder", index, "extraMaxMs"],
          message: `Bot wait ladder: the step from ${step.fromPaidDuels} paid duels must wait less than the queue safety TTL. Use the lifetime cap to keep users off bots.`,
        });
      }
    });
    if (config.botDuelMinLifetimeCap > config.botDuelMaxLifetimeCap) {
      ctx.addIssue({
        code: "custom",
        path: ["botDuelMinLifetimeCap"],
        message: "Lifetime bot-duel cap: min must not exceed max.",
      });
    }
    // Off is expressed by BOTH ends at 0. A 0 low end with a live high end would
    // draw a 0 cap for some users (no bot duel ever) and a real one for others.
    if (config.botDuelMinLifetimeCap === 0 && config.botDuelMaxLifetimeCap > 0) {
      ctx.addIssue({
        code: "custom",
        path: ["botDuelMinLifetimeCap"],
        message: "Lifetime bot-duel cap: set both ends to 0 to disable, or a min of at least 1.",
      });
    }
  });
export type UpdateCanberraiConfigBody = z.infer<typeof UpdateCanberraiConfigBodySchema>;

/** The three postures the CanberrAI strength loop can put a bot in, weakest first. */
export const CanberraiDuelPostureSchema = z.enum(["GENTLE", "RESTRAINED", "STRONG"]);
export type CanberraiDuelPostureDto = z.infer<typeof CanberraiDuelPostureSchema>;

/**
 * Which clause of the strength loop produced a duel's posture — the half of the
 * decision no recompute can recover, since retuning the loop changes what the
 * same history yields.
 *
 * `DECISIVE` the signal alone earned it · `HELD` the signal decayed but not past
 * the hold threshold, so the posture already in play carried · `BASE` the signal
 * holds nothing, so the middle posture applies · `COLD_START` a stakeless tier
 * with a partial window · `UNREADABLE` the opponent's record could not be read,
 * which deliberately plays the gentlest posture and is NOT a newcomer.
 */
export const CanberraiPostureReasonSchema = z.enum(["DECISIVE", "HELD", "BASE", "COLD_START", "UNREADABLE"]);
export type CanberraiPostureReasonDto = z.infer<typeof CanberraiPostureReasonSchema>;

/** One recorded decision: one BOT in one duel, so a two-bot duel appears twice. */
export const CanberraiPostureDecisionDtoSchema = z.object({
  /** ISO timestamp of the decision — session boot, i.e. the duel's start. */
  decidedAt: z.string(),
  campaignName: z.string(),
  botHubUserId: z.number().int(),
  /** The bot account's arena nickname — the face the player saw. */
  botNickname: z.string().nullable(),
  tier: ArenaEntryTierSchema.nullable(),
  /**
   * The posture the bot was given. A bot-vs-bot duel then handicaps its weaker slots
   * below it, and that draw is not stored, so this is not what every bot ran.
   */
  posture: CanberraiDuelPostureSchema,
  reason: CanberraiPostureReasonSchema,
  /** The posture the hysteresis was evaluated against; `null` when no replay step ran. */
  postureBefore: CanberraiDuelPostureSchema.nullable(),
  /** The control signal at the deciding step; `null` exactly when `postureBefore` is. */
  signal: z.number().nullable(),
  /** Wins out of the signal's window. `null` only when the record was unreadable. */
  window: z.object({ wins: z.number().int(), duels: z.number().int() }).nullable(),
  /** Duels of history the replay ran over. `null` only when the record was unreadable. */
  historyDuels: z.number().int().nullable(),
  /**
   * False when another entrant's reading set `posture`. A duel is sized by its toughest
   * player, so this row's reading is usually the softer one — except that an unreadable
   * record decides outright and reads GENTLE (so `posture` can be the softer one), and
   * a tie records the first entrant (so the two can be equal).
   */
  setByThisPlayer: z.boolean(),
  /** This player's own reading. Differs from `posture` only when `setByThisPlayer` is false. */
  playerPosture: CanberraiDuelPostureSchema,
  /** `null` = nothing measured: the epoch has not finalized, or neither side traded. */
  playerWon: z.boolean().nullable(),
  /**
   * The posture of the player's previous DUEL — the nearest older row from another
   * campaign, since a two-bot duel writes a sibling row right next to this one.
   * `null` at the start of their record, or once the page's lookahead runs out.
   * `changed` is derived from it, so a harder/easier arrow cannot contradict the label.
   */
  previousDuelPosture: CanberraiDuelPostureSchema.nullable(),
  /** True when `posture` differs from `previousDuelPosture`. */
  changed: z.boolean(),
});
export type CanberraiPostureDecisionDto = z.infer<typeof CanberraiPostureDecisionDtoSchema>;

/**
 * The loop's CURRENT constants, so the page can draw the band a signal sat in
 * without hardcoding it. Current, not historical — a row's `reason` is the
 * authority on what happened; these only frame it.
 */
export const CanberraiStrengthLoopDtoSchema = z.object({
  windowDuels: z.number().int(),
  replayDuels: z.number().int(),
  gain: z.number(),
  targetWinRateMin: z.number(),
  targetWinRateMax: z.number(),
  /** |signal| at or above which a posture is entered on its own. */
  enterAt: z.number(),
  /** |signal| below which a posture can no longer be held. */
  holdAt: z.number(),
});
export type CanberraiStrengthLoopDto = z.infer<typeof CanberraiStrengthLoopDtoSchema>;

/**
 * `GET /v1/cms/canberrai-config/player-difficulty` — one player's bot-difficulty
 * history, newest first.
 *
 * `player: null` means the identifier matched nothing; the lookup is exact on a
 * unique index, so a typo finds nobody rather than running a slow scan.
 * An empty `decisions` with a resolved player means the bot played them only
 * before this record existed — nothing is backfilled, because a recompute would
 * present today's loop constants as history.
 */
export const CanberraiPlayerDifficultyDtoSchema = z.object({
  /** Which identifier the input was read as, or `null` when it named nothing resolvable. */
  resolvedBy: z.enum(["HUB_USER_ID", "DEFI_ADDRESS", "EMAIL", "NICKNAME"]).nullable(),
  player: z
    .object({
      hubUserId: z.number().int(),
      nickname: z.string().nullable(),
      defiAddress: z.string().nullable(),
      email: z.string().nullable(),
      /** This account is in the CanberrAI bot pool — the rows below are bot-vs-bot. */
      isBot: z.boolean(),
    })
    .nullable(),
  decisions: z.array(CanberraiPostureDecisionDtoSchema),
  /** More decisions exist past this page, so the oldest row shown is not the oldest there is. */
  hasMore: z.boolean(),
  loop: CanberraiStrengthLoopDtoSchema,
});
export type CanberraiPlayerDifficultyDto = z.infer<typeof CanberraiPlayerDifficultyDtoSchema>;

/** Page size cap for the bot-difficulty series — one index-ordered page, never a full history. */
export const CANBERRAI_PLAYER_DIFFICULTY_MAX_LIMIT = 100;

/**
 * Bot Spin & Go table scheduler config (simulated player liquidity) — backed by
 * the singleton `arena_bot_scheduler_configs` row, edited from the
 * `/arena-entries` page of internal-cms. The cron opens bot-only 1v1v1 tables at
 * Poisson-jittered times converging to `targetCompetitionsPerDay`. Only PACING
 * is configured here: each table uses the same entry commands, buy-ins and duel
 * pipeline as the tables humans play, so it can never drift from them.
 */
/** Sanity ceiling on the daily target (one spawn per 30s) — a fat-fingered CMS value must not mass-spawn public competitions. */
export const ARENA_BOT_SCHEDULER_MAX_TARGET_PER_DAY = 2880;

/** 24 relative weights (index = UTC hour) shaping intra-day arrival density; empty ⇒ uniform. Shared by the CMS write path and the API's stored-row validation. */
export const ArenaBotSchedulerHourlyWeightsSchema = z
  // Weights are relative, so magnitude is meaningless — the cap only keeps the
  // planner's weight sum finite (huge values would overflow to Infinity and
  // silently zero every hourly rate).
  .array(z.number().nonnegative().max(1000))
  .refine((weights) => weights.length === 0 || weights.length === 24, {
    error: "hourlyWeights must be empty (uniform) or exactly 24 entries (one per UTC hour)",
  })
  .refine((weights) => weights.length === 0 || weights.some((weight) => weight > 0), {
    error: "at least one hourly weight must be positive",
  });

export const ArenaBotSchedulerConfigDtoSchema = z.object({
  /** Primary lever: average bot competitions per day. 0 ⇒ scheduler idle. */
  targetCompetitionsPerDay: z.number().int().nonnegative().max(ARENA_BOT_SCHEDULER_MAX_TARGET_PER_DAY),
  /**
   * Optional time-of-day weighting: 24 relative weights (index = UTC hour).
   * Empty ⇒ uniform arrival density across the day.
   */
  hourlyWeights: ArenaBotSchedulerHourlyWeightsSchema,
  /**
   * Read-only: resolved value of the
   * `cede_arena:api:canberrai_bot_competitions_enable` feature flag. When
   * `false` the scheduler cron is a no-op and the CMS locks this section
   * behind an "enable the flag first" prompt. Ignored on write.
   */
  botCompetitionsEnabled: z.boolean(),
});
export type ArenaBotSchedulerConfigDto = z.infer<typeof ArenaBotSchedulerConfigDtoSchema>;

/**
 * Arena traffic curve: the "when players are online" widget on `/play`.
 * Nudge-only: it never gates matchmaking, it just tells players when the queue
 * is densest so more of them meet a human instead of a bot.
 *
 * Two modes, switchable from Solo so the two can be A/B'd:
 * - `HISTORICAL` fits a circular Gaussian to real human queue joins.
 * - `MANUAL` draws the admin's own Gaussian, which can be deliberately tighter
 *   than reality to give players a meeting point to converge on.
 */
export const ARENA_TRAFFIC_CURVE_MODES = ["HISTORICAL", "MANUAL"] as const;
export const ArenaTrafficCurveModeSchema = z.enum(ARENA_TRAFFIC_CURVE_MODES);
export type ArenaTrafficCurveMode = z.infer<typeof ArenaTrafficCurveModeSchema>;

export const ARENA_TRAFFIC_CURVE_HOURS_PER_DAY = 24;

/** Widest useful spread: past half the circle a wrapped Gaussian is indistinguishable from a flat day. */
export const ARENA_TRAFFIC_CURVE_MAX_SIGMA_HOURS = ARENA_TRAFFIC_CURVE_HOURS_PER_DAY / 2;
/** Narrowest: an hourly histogram cannot resolve finer than half a bucket. */
export const ARENA_TRAFFIC_CURVE_MIN_SIGMA_HOURS = 0.5;
/** A year of lookback already smooths across seasons; more only adds query cost. */
export const ARENA_TRAFFIC_CURVE_MAX_LOOKBACK_DAYS = 365;

/** 24 relative weights (index = UTC hour), peak normalized to 1. The client rotates them into its own timezone. */
export const ArenaTrafficCurveHourlyWeightsSchema = z
  .array(z.number().min(0).max(1))
  .refine((weights) => weights.length === 24, {
    error: "hourlyWeights must be exactly 24 entries (one per UTC hour)",
  });

/**
 * `GET /v1/cede-arena/traffic-curve`. Discriminated on `status` so a window
 * with too little history reports itself instead of returning a curve fitted
 * to noise — the widget hides rather than inventing a peak.
 */
export const ArenaTrafficCurveDtoSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("READY"),
    mode: ArenaTrafficCurveModeSchema,
    hourlyWeights: ArenaTrafficCurveHourlyWeightsSchema,
    /** Whole-hour peak of the drawn curve, UTC. */
    peakHourUtc: z.number().int().min(0).max(23),
    /** Joins behind a HISTORICAL fit; null in MANUAL mode, which reads no history. */
    sampleSize: z.number().int().nonnegative().nullable(),
    /**
     * Circular resultant 0–1 of a HISTORICAL fit: how peaked the real day is.
     * Null in MANUAL mode. Surfaced so a near-flat history is visible as a weak
     * curve rather than passing for a confident peak.
     */
    concentration: z.number().min(0).max(1).nullable(),
    showLiveQueue: z.boolean(),
  }),
  z.object({
    status: z.literal("DISABLED"),
  }),
  z.object({
    status: z.literal("INSUFFICIENT_DATA"),
    sampleSize: z.number().int().nonnegative(),
    minSampleSize: z.number().int().positive(),
  }),
]);
export type ArenaTrafficCurveDto = z.infer<typeof ArenaTrafficCurveDtoSchema>;

/** `GET /v1/cms/arena-traffic-curve-config` — the stored knobs plus a preview of what they currently draw. */
export const ArenaTrafficCurveConfigDtoSchema = z.object({
  enabled: z.boolean(),
  mode: ArenaTrafficCurveModeSchema,
  lookbackDays: z.number().int().positive().max(ARENA_TRAFFIC_CURVE_MAX_LOOKBACK_DAYS),
  minSampleSize: z.number().int().positive(),
  manualPeakHourUtc: z.number().int().min(0).max(23),
  manualSigmaHours: z.number().min(ARENA_TRAFFIC_CURVE_MIN_SIGMA_HOURS).max(ARENA_TRAFFIC_CURVE_MAX_SIGMA_HOURS),
  showLiveQueue: z.boolean(),
  /**
   * Read-only: the live HISTORICAL fit, in either mode, so an admin tuning MANUAL
   * sees how far the invented curve sits from the real one. Deliberately NOT
   * gated on `minSampleSize` — the CMS applies that gate itself so it tracks the
   * unsaved field. Null only when the histogram has no direction at all.
   */
  historicalFit: z
    .object({
      peakHourUtc: z.number().min(0).lt(ARENA_TRAFFIC_CURVE_HOURS_PER_DAY),
      sigmaHours: z.number().positive(),
      concentration: z.number().min(0).max(1),
      sampleSize: z.number().int().nonnegative(),
    })
    .nullable(),
});
export type ArenaTrafficCurveConfigDto = z.infer<typeof ArenaTrafficCurveConfigDtoSchema>;

/** Body for `PUT /v1/cms/arena-traffic-curve-config`. */
export const UpdateArenaTrafficCurveConfigBodySchema = ArenaTrafficCurveConfigDtoSchema.omit({
  historicalFit: true,
});
export type UpdateArenaTrafficCurveConfigBody = z.infer<typeof UpdateArenaTrafficCurveConfigBodySchema>;

/** Body for `PUT /v1/cms/arena-bot-scheduler-config`. */
export const UpdateArenaBotSchedulerConfigBodySchema = ArenaBotSchedulerConfigDtoSchema.omit({
  botCompetitionsEnabled: true,
});
export type UpdateArenaBotSchedulerConfigBody = z.infer<typeof UpdateArenaBotSchedulerConfigBodySchema>;

/**
 * What a scan of the printed Arena event QR returns. The QR encodes a
 * stable API URL; this single knob decides the response server-side so
 * the printed card never has to change:
 * - `CALENDAR` serves a `text/calendar` (.ics) invite built from the config.
 * - `REDIRECT` 302s to `targetUrl` (e.g. the live competition page once it exists).
 */
export const ArenaEventResponseModeSchema = z.enum(["CALENDAR", "REDIRECT"]);
export type ArenaEventResponseMode = z.infer<typeof ArenaEventResponseModeSchema>;

/**
 * Singleton config behind the printed Arena event QR. Edited from the
 * `/arena-entries` page of `apps/admin/internal-cms`; read by the public
 * `GET /v1/arena-event/scan` endpoint on every scan. `startsAt`/`endsAt`
 * are ISO-8601 UTC strings; `targetUrl` is the competition link embedded
 * in the invite and used as the redirect target.
 */
export const ArenaEventConfigDtoSchema = z.object({
  // Stable per-event slug bumped by the admin for each new competition
  // (e.g. `comp-1`, `comp-2`). Snapshotted onto every scan so reach stats
  // split cleanly per event, independent of the editable display title.
  eventKey: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, { error: "eventKey must be a lowercase slug (a-z, 0-9, hyphen)" }),
  title: z.string().min(1).max(200),
  description: z.string().max(5000),
  location: z.string().max(500),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  targetUrl: z.url(),
  responseMode: ArenaEventResponseModeSchema,
});
export type ArenaEventConfigDto = z.infer<typeof ArenaEventConfigDtoSchema>;

/** Body for `PUT /v1/cms/arena-event-settings`. */
export const UpdateArenaEventConfigBodySchema = ArenaEventConfigDtoSchema.refine(
  (c) => new Date(c.endsAt).getTime() > new Date(c.startsAt).getTime(),
  { error: "endsAt must be after startsAt", path: ["endsAt"] },
);
export type UpdateArenaEventConfigBody = z.infer<typeof UpdateArenaEventConfigBodySchema>;

/**
 * Lifecycle + outbox state for an ArenaEntry. Drives every UI step on the Hub
 * and the filter chips in the admin CMS. The `_NOTIFIED` suffix variants are
 * outbox-internal; the UI collapses them with their parent state (a PENDING
 * row and a PENDING_NOTIFIED row both render as "Awaiting payment").
 */
export const ArenaEntryStatusSchema = z.enum([
  "PENDING",
  "PENDING_NOTIFIED",
  "PAID",
  "PAID_NOTIFIED",
  "MATCH_PROPOSED",
  "ASSIGNED",
  "ASSIGNED_NOTIFIED",
  "CANCELLED",
]);
export type ArenaEntryStatusDto = z.infer<typeof ArenaEntryStatusSchema>;

/**
 * The statuses that hold the user's one queue slot, so at most one entry in a
 * me-entry payload matches. ASSIGNED is already a duel but still holds the slot
 * until its notify job runs; ASSIGNED_NOTIFIED is a live duel and frees it.
 * Must equal the API's `ARENA_ENTRY_ACTIVE_STATUSES` (the one-slot unique index).
 */
export const ARENA_QUEUED_ENTRY_STATUSES = [
  "PENDING",
  "PENDING_NOTIFIED",
  "PAID",
  "PAID_NOTIFIED",
  "MATCH_PROPOSED",
  "ASSIGNED",
] as const satisfies readonly ArenaEntryStatusDto[];

/** True when the entry holds the user's one queue slot (see `ARENA_QUEUED_ENTRY_STATUSES`). */
export const isArenaEntryQueued = (status: ArenaEntryStatusDto): boolean =>
  (ARENA_QUEUED_ENTRY_STATUSES as readonly ArenaEntryStatusDto[]).includes(status);

export const ArenaEntryCancellationReasonSchema = z.enum([
  "PAYMENT_TIMEOUT",
  "ADMIN_CANCELLED",
  "READY_CHECK_TIMEOUT",
  "USER_CANCELLED",
  "QUEUE_TIMEOUT",
]);
export type ArenaEntryCancellationReasonDto = z.infer<typeof ArenaEntryCancellationReasonSchema>;

/**
 * Single ArenaEntry view, used by both the Hub `GET /me/entry` endpoint and
 * the admin CMS list. Numeric amounts are strings (micro-USDT — exceeds
 * JS safe-int for some types). Timestamps are ISO strings.
 */
/** A Spin & Go table the entry is seated at while it fills. */
export const ArenaEntryTableDtoSchema = z.object({
  seatsFilled: z.number().int().min(1),
  seatsTotal: z.number().int().min(2),
});
export type ArenaEntryTableDto = z.infer<typeof ArenaEntryTableDtoSchema>;

export const ArenaEntryDtoSchema = z.object({
  id: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
  type: ArenaEntryTypeSchema,
  status: ArenaEntryStatusSchema,
  /** Stake/matchmaking tier. FE branches the waiting-room odds display on `SPIN_AND_GO`. */
  tier: ArenaEntryTierSchema,
  /** Wallet snapshot at opt-in time; null for email-only users without a linked wallet. */
  wallet: z.string().nullable(),
  /** Owner's sign-in email — only populated on the admin list endpoint. */
  email: z.string().nullable().optional(),
  feeMicroUsdt: z.string(),
  depositAddress: z.string(),
  createdAt: z.string(),
  txHash: z.string().nullable(),
  paidAt: z.string().nullable(),
  assignedCampaignName: z.string().nullable(),
  /**
   * The assigned duel campaign's start (ISO) — the entering overlay's countdown
   * target. Mirrors the ASSIGNED SSE's `campaignStartsAt` so a poll-only client that
   * missed the SSE frame can still drive the countdown. Null unless assigned.
   */
  assignedCampaignStartsAt: z.string().nullable(),
  /**
   * The assigned campaign's drawn Spin & Go pot factor, buy-in multiple + total
   * collected buy-ins (decimal strings). Lets the countdown-stage wheel resolve the
   * drawn multiple from the entry (with `tier` + the public odds config) instead of a
   * racy client-side campaign-by-name fetch. Null unless assigned (multiples also null
   * until drawn / on a non-spin duel).
   */
  assignedSpinAndGoPotFactor: z.string().nullable(),
  /** Optional for the deploy window: an older API omits it. */
  assignedSpinAndGoBuyInMultiple: z.string().nullish(),
  assignedSpinAndGoCollectedUsdt: z.string().nullable(),
  assignedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancellationReason: ArenaEntryCancellationReasonSchema.nullable(),
  discordUsername: z.string().nullable(),
  discordUserId: z.string().nullable(),
  /**
   * The amount this match actually plays at (micro-USDT string), joined from the
   * entry's active proposal — null when no proposal (waiting in queue, cancelled).
   * Equals `feeMicroUsdt` for a same-stake match; LOWER when matchmaking paired
   * this entry down a buy-in (its buy-in's `downgradeCutoffMs` elapsed). The FE derives the
   * downgrade warning via Decimal compare (`matched < fee`); the locked excess is
   * released back to the free balance when the duel is assigned. Defaulted so a
   * pre-ladder backend payload keeps parsing during deploys.
   */
  matchedFeeMicroUsdt: z.string().nullable().default(null),
  /**
   * Non-null while the entry sits at a Spin & Go table (status `MATCH_PROPOSED`,
   * no ready check), full tables included, until the duel is assigned: the FE stays
   * on the waiting screen and shows the seat count. Null for any 1v1 proposal.
   * Defaulted so a backend payload predating tables keeps parsing during deploys.
   */
  table: ArenaEntryTableDtoSchema.nullable().default(null),
  /**
   * Ready-check context, joined from the entry's active match
   * proposal. All null unless `status === "MATCH_PROPOSED"` (opponent is also
   * kept on ASSIGNED/_NOTIFIED so the matched UI can name the opponent).
   */
  readyDeadlineAt: z.string().nullable(),
  /** Non-null once the user confirmed their side ("waiting for opponent"). */
  readyConfirmedAt: z.string().nullable(),
  /**
   * True when the active proposal confirmed every side at propose time
   * (`cede_arena:api:auto_accept_match_enable`): nobody was asked, so the modal
   * states the match instead of offering it and holds until `readyDeadlineAt`
   * rather than the standard lock-in beat. A mixed proposal, where one side was
   * matched at a buy-in or tier it did not queue for and still confirms by
   * hand, reads false for both sides. Optional for the deploy window (old API
   * bundles don't send it); absent reads as false.
   */
  autoAccepted: z.boolean().optional(),
  /**
   * True when THIS side was confirmed for at propose time. Differs from
   * `autoAccepted` on a mixed proposal, where this side was never asked but its
   * opponent still has a decision open. It is what the match-found alert keys
   * on: a side that skipped the ready check entirely gets no other announcement.
   * Optional for the deploy window; absent reads as false.
   */
  readyAutoConfirmed: z.boolean().optional(),
  opponent: ArenaMatchOpponentDtoSchema.nullable(),
  /**
   * True when this entry's active proposal is a directed rematch (has a source
   * campaign), so the FE can render the rematch flow inline in the recap instead
   * of the ready-check modal.
   */
  isRematch: z.boolean(),
  /**
   * The duel a directed rematch replays; a recap owns the rematch only when this
   * is its campaign. Optional for the deploy window; absent reads as no source.
   */
  rematchSourceCampaignName: z.string().nullable().optional(),
  /** Re-queue cooldown after an opponent no-show; informational for the FE. */
  rematchCooldownUntil: z.string().nullable(),
  /**
   * TRUE when a free-entry ticket funds this entry (DEV-651): the fee is
   * covered by Cede (no balance debit) and the FE renders "Free ticket entry"
   * in place of the buy-in. Optional for the deploy window (old API bundles
   * don't send it); absent reads as false.
   */
  fundedByTicket: z.boolean().optional(),
  /**
   * TRUE when the other side of this entry's proposal is ticket-funded. Two
   * ticket entries never meet, so a directed-rematch invitee must accept with its
   * balance. Optional for the deploy window; absent reads as false.
   */
  opponentFundedByTicket: z.boolean().optional(),
});
export type ArenaEntryDto = z.infer<typeof ArenaEntryDtoSchema>;

const KNOWN_ARENA_TICKET_SOURCES = [
  "STARTER_PACK",
  "ADMIN_GRANT",
  "PARTNER_LINK",
  "QUEST",
  "COMPETITION_PRIZE",
] as const;
export type ArenaTicketSource = (typeof KNOWN_ARENA_TICKET_SOURCES)[number] | "UNKNOWN";

/**
 * Where a ticket came from. Read tolerantly: a source added on the API before
 * this bundle shipped must not fail the parse and take the reader's WHOLE ticket
 * list down with it — the tickets are spendable whether or not their provenance
 * is nameable here. An unrecognized value reads as UNKNOWN and never as one of
 * the named sources, so no consumer can mistake it for a known origin.
 */
const ArenaTicketSourceSchema = z
  .string()
  .transform(
    (value): ArenaTicketSource =>
      (KNOWN_ARENA_TICKET_SOURCES as readonly string[]).includes(value) ? (value as ArenaTicketSource) : "UNKNOWN",
  );

/** A spendable free-entry ticket (DEV-651): face value unlocks exactly one PAID buy-in amount. */
export const ArenaTicketDtoSchema = z.object({
  id: z.string(),
  /** Decimal-USDT face value, canonical ladder form (e.g. "0.5", "5"). */
  faceValueUsdt: z.string(),
  /** ISO expiry instant — the FE counts down to it and drops the ticket at zero. */
  expiresAt: z.string(),
  source: ArenaTicketSourceSchema,
});
export type ArenaTicketDto = z.infer<typeof ArenaTicketDtoSchema>;

/** Response for `GET /v1/cede-arena/tickets`: the user's spendable tickets, soonest-expiring first. */
export const GetMyArenaTicketsResponseSchema = z.object({
  tickets: ArenaTicketDtoSchema.array(),
});
export type GetMyArenaTicketsResponse = z.infer<typeof GetMyArenaTicketsResponseSchema>;

/**
 * A free-entry ticket / denomination face value: a positive decimal-USDT string
 * with up to 6 decimals. Shared by the starter-pack, denomination-config, and
 * admin-grant bodies so "0" and negatives are rejected in one place.
 */
const TicketFaceValueUsdtSchema = z
  .string()
  .regex(/^\d+(\.\d{1,6})?$/, "faceValueUsdt must be a decimal string with up to 6 decimals")
  .refine((value) => new Decimal(value).gt(0), "faceValueUsdt must be greater than 0");

/** Canonical form of a face value so "0.50" and "0.5" collide in dedup checks (Decimal, not float). */
const canonicalFaceValue = (faceValueUsdt: string): string => new Decimal(faceValueUsdt).toString();

/**
 * Cede funds every signup pack and its tickets pay out as withdrawable USDT, so
 * a CMS typo is spend. Both bounds are per-pack: at most this many tickets on
 * one line, and at most this much total face value across the pack — the pack
 * mints on every claim, and the per-claim cost is what the bounds hold down.
 */
export const ARENA_SIGNUP_PACK_MAX_ITEM_QUANTITY = 50;
export const ARENA_SIGNUP_PACK_MAX_TOTAL_VALUE_USDT = 1000;

/**
 * Ticket count in a signup pack line — shared by the starter pack and partner
 * links. WRITE side only: a pack persisted before the cap existed still has to
 * read, or the CMS page that would let an admin fix it never renders.
 */
const SignupPackQuantitySchema = z.number().int().positive().max(ARENA_SIGNUP_PACK_MAX_ITEM_QUANTITY);

/**
 * The two rules a replace-all pack write must satisfy: one line per denomination,
 * and a total face value under the ceiling (`sum(faceValue x quantity)`, Decimal —
 * money is never summed as float). The message names the computed total so an
 * admin sees what they actually typed.
 */
const refineSignupPackItems = (
  items: Array<{ faceValueUsdt: string; quantity: number }>,
  ctx: z.RefinementCtx,
): void => {
  const seen = new Set<string>();
  for (const item of items) {
    const canonical = canonicalFaceValue(item.faceValueUsdt);
    if (seen.has(canonical)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["items"],
        message: `duplicate face value ${item.faceValueUsdt}`,
      });
    }
    seen.add(canonical);
  }

  const totalValueUsdt = items.reduce(
    (total, item) => total.plus(new Decimal(item.faceValueUsdt).times(item.quantity)),
    new Decimal(0),
  );
  if (totalValueUsdt.gt(ARENA_SIGNUP_PACK_MAX_TOTAL_VALUE_USDT)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["items"],
      message: `pack is worth ${totalValueUsdt.toString()} USDT, over the ${ARENA_SIGNUP_PACK_MAX_TOTAL_VALUE_USDT} USDT limit`,
    });
  }
};

/**
 * One line of the new-user starter pack (DEV-651): `quantity` free-entry
 * tickets at `faceValueUsdt`, each expiring `expiryDurationMs` after grant.
 * Face values must reference an enabled ArenaTicketConfiguration denomination
 * (server-validated on save).
 */
export const ArenaStarterPackItemDtoSchema = z.object({
  faceValueUsdt: TicketFaceValueUsdtSchema,
  quantity: z.number().int().positive(),
  expiryDurationMs: z.number().int().positive(),
  sortOrder: z.number().int().nonnegative(),
});
export type ArenaStarterPackItemDto = z.infer<typeof ArenaStarterPackItemDtoSchema>;

/** The same line on the way IN, where the per-pack bounds apply. */
const ArenaStarterPackItemBodySchema = ArenaStarterPackItemDtoSchema.extend({ quantity: SignupPackQuantitySchema });

/** `GET /v1/cms/arena-starter-pack` response. Empty `items` = pack disabled. */
export const GetArenaStarterPackResponseSchema = z.object({
  items: ArenaStarterPackItemDtoSchema.array(),
});
export type GetArenaStarterPackResponse = z.infer<typeof GetArenaStarterPackResponseSchema>;

/** Body for `PUT /v1/cms/arena-starter-pack` — replace-all write of the pack composition. */
export const UpdateArenaStarterPackBodySchema = z
  .object({
    items: ArenaStarterPackItemBodySchema.array(),
  })
  .superRefine((data, ctx) => refineSignupPackItems(data.items, ctx));
export type UpdateArenaStarterPackBody = z.infer<typeof UpdateArenaStarterPackBodySchema>;

/**
 * One line of the onboarding-quest reward pack (DEV-685): `quantity` free-entry
 * tickets at `faceValueUsdt`, each expiring `expiryDurationMs` after grant.
 * Mirrors the starter pack exactly — same denomination + bound rules — since
 * both are CMS-configured, Cede-funded ticket packs.
 */
export const ArenaQuestRewardItemDtoSchema = z.object({
  faceValueUsdt: TicketFaceValueUsdtSchema,
  quantity: z.number().int().positive(),
  expiryDurationMs: z.number().int().positive(),
  sortOrder: z.number().int().nonnegative(),
});
export type ArenaQuestRewardItemDto = z.infer<typeof ArenaQuestRewardItemDtoSchema>;

/** The same line on the way IN, where the per-pack bounds apply. */
const ArenaQuestRewardItemBodySchema = ArenaQuestRewardItemDtoSchema.extend({ quantity: SignupPackQuantitySchema });

/** `GET /v1/cms/cede-arena/quest-reward` response. Empty `items` = quest reward disabled. */
export const GetArenaQuestRewardResponseSchema = z.object({
  items: ArenaQuestRewardItemDtoSchema.array(),
});
export type GetArenaQuestRewardResponse = z.infer<typeof GetArenaQuestRewardResponseSchema>;

/** Body for `PUT /v1/cms/cede-arena/quest-reward` — replace-all write of the reward pack composition. */
export const UpdateArenaQuestRewardBodySchema = z
  .object({
    items: ArenaQuestRewardItemBodySchema.array(),
  })
  .superRefine((data, ctx) => refineSignupPackItems(data.items, ctx));
export type UpdateArenaQuestRewardBody = z.infer<typeof UpdateArenaQuestRewardBodySchema>;

/**
 * Onboarding-quest progress, fully derived on read (DEV-685) — there is no
 * persisted quest row. `GRANTED` wins over the clock; `INACTIVE` short-circuits
 * everything else (flag off, or ops configured no reward). `PENDING` is the
 * post-deadline state while a qualifying duel is still finalizing, so the FE
 * doesn't flash EXPIRED on a grant that is still legitimately coming.
 */
export const ArenaQuestStatusSchema = z.enum(["ACTIVE", "PENDING", "GRANTED", "EXPIRED", "INACTIVE"]);
export type ArenaQuestStatus = z.infer<typeof ArenaQuestStatusSchema>;

/** Response for `GET /v1/cede-arena/quest/state`. */
export const GetMyArenaQuestStateResponseSchema = z.object({
  status: ArenaQuestStatusSchema,
  /** ISO instant of `signup.createdAt + 24h` — the quest deadline. `null` for `INACTIVE` (flag off or unconfigured — there is nothing to count down to). */
  expiresAt: z.string().nullable(),
  depositDone: z.boolean(),
  /** In-window deposit sum, decimal USDT (e.g. "25" for "$25.00 deposited"). */
  depositedUsdt: z.string(),
  competitionDone: z.boolean(),
  /** Decimal-USDT qualifying buy-in, so the FE never hardcodes "10". */
  requiredBuyInUsdt: z.string(),
  reward: z.array(z.object({ faceValueUsdt: z.string(), quantity: z.number().int().nonnegative() })),
});
export type GetMyArenaQuestStateResponse = z.infer<typeof GetMyArenaQuestStateResponseSchema>;

/**
 * One available free-entry ticket denomination (DEV-651). The dedicated source
 * of truth for ticket values — independent of the paid buy-in ladder. Disabled
 * rows are hidden from the starter-pack picker and rejected at grant / opt-in.
 */
export const ArenaTicketConfigurationItemDtoSchema = z.object({
  faceValueUsdt: TicketFaceValueUsdtSchema,
  enabled: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
});
export type ArenaTicketConfigurationItemDto = z.infer<typeof ArenaTicketConfigurationItemDtoSchema>;

/** `GET /v1/cms/arena-ticket-configuration` response. Empty `items` = no ticket denominations configured. */
export const GetArenaTicketConfigurationResponseSchema = z.object({
  items: ArenaTicketConfigurationItemDtoSchema.array(),
});
export type GetArenaTicketConfigurationResponse = z.infer<typeof GetArenaTicketConfigurationResponseSchema>;

/** Body for `PUT /v1/cms/arena-ticket-configuration` — replace-all write of the available ticket values. */
export const UpdateArenaTicketConfigurationBodySchema = z
  .object({
    items: ArenaTicketConfigurationItemDtoSchema.array(),
  })
  .superRefine((data, ctx) => {
    const seen = new Set<string>();
    for (const item of data.items) {
      const canonical = canonicalFaceValue(item.faceValueUsdt);
      if (seen.has(canonical)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["items"],
          message: `duplicate face value ${item.faceValueUsdt}`,
        });
      }
      seen.add(canonical);
    }
  });
export type UpdateArenaTicketConfigurationBody = z.infer<typeof UpdateArenaTicketConfigurationBodySchema>;

/**
 * Body for `POST /v1/cms/arena-tickets/grant` — an admin hands `quantity`
 * tickets of `faceValueUsdt` to one user, each expiring `expiresInDays` from
 * now. Face value must be an enabled ArenaTicketConfiguration denomination
 * (server-validated).
 */
export const GrantArenaTicketsBodySchema = z.object({
  hubUserId: z.number().int().positive(),
  faceValueUsdt: TicketFaceValueUsdtSchema,
  quantity: z.number().int().positive().max(100),
  expiresInDays: z.number().int().positive().max(3650),
});
export type GrantArenaTicketsBody = z.infer<typeof GrantArenaTicketsBodySchema>;

/** One free-entry ticket in the admin per-user view (DEV-651) — any status. */
export const AdminArenaTicketDtoSchema = z.object({
  id: z.string(),
  faceValueUsdt: z.string(),
  status: z.enum(["ACTIVE", "USED", "EXPIRED"]),
  source: ArenaTicketSourceSchema,
  /** ISO instants. `usedAt` is null unless the ticket funded an entry. */
  expiresAt: z.string(),
  usedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminArenaTicketDto = z.infer<typeof AdminArenaTicketDtoSchema>;

/**
 * Response for `GET /v1/cms/arena-tickets/user?hubUserId=` and the grant POST:
 * ALL of the user's tickets (active / used / expired), newest first.
 */
export const GetArenaTicketsForUserResponseSchema = z.object({
  tickets: AdminArenaTicketDtoSchema.array(),
});
export type GetArenaTicketsForUserResponse = z.infer<typeof GetArenaTicketsForUserResponseSchema>;

/**
 * Slug of a partner / KOL ticket link — the URL segment served at `/i/<slug>`.
 * Lowercase alphanumerics joined by single dashes.
 */
export const ArenaTicketLinkSlugSchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "slug must be lowercase alphanumerics separated by single dashes");

/**
 * One line of a partner ticket-link pack: `quantity` free-entry tickets at
 * `faceValueUsdt`, each expiring `expiryDurationMs` after the claim. Face
 * values must reference an enabled ArenaTicketConfiguration denomination
 * (server-validated on save and re-validated at grant).
 */
export const ArenaTicketLinkItemDtoSchema = z.object({
  faceValueUsdt: TicketFaceValueUsdtSchema,
  quantity: z.number().int().positive(),
  expiryDurationMs: z.number().int().positive(),
  sortOrder: z.number().int().nonnegative(),
});
export type ArenaTicketLinkItemDto = z.infer<typeof ArenaTicketLinkItemDtoSchema>;

const TEN_YEARS_MS = 10 * 365 * 24 * 60 * 60 * 1000;

/** The same line on the way IN, where the bounds apply. */
const ArenaTicketLinkItemBodySchema = ArenaTicketLinkItemDtoSchema.extend({
  quantity: SignupPackQuantitySchema,
  /** Capped at 10 years: `now + expiryDurationMs` must stay a date Prisma can store. */
  expiryDurationMs: z.number().int().positive().max(TEN_YEARS_MS),
});

/** Why a ticket link is not claimable. `EXHAUSTED` = `maxClaims` reached. */
export const ArenaTicketLinkStatusSchema = z.enum(["ACTIVE", "DISABLED", "EXHAUSTED"]);
export type ArenaTicketLinkStatus = z.infer<typeof ArenaTicketLinkStatusSchema>;

/**
 * Response for the unauthenticated `GET /v1/cede-arena/ticket-links/:slug` —
 * what the `/i/<slug>` landing page advertises before the claim. A non-ACTIVE
 * status still resolves (the page states the offer has closed) rather than 404ing,
 * so a shared link never dead-ends.
 */
export const ArenaTicketLinkPreviewResponseSchema = z.object({
  slug: ArenaTicketLinkSlugSchema,
  partnerName: z.string(),
  /** Optional custom hero line; null falls back to the default landing copy. */
  headline: z.string().nullable(),
  status: ArenaTicketLinkStatusSchema,
  items: z.array(z.object({ faceValueUsdt: z.string(), quantity: z.number().int().nonnegative() })),
  ticketCount: z.number().int().nonnegative(),
  totalValueUsdt: z.string(),
  claimCount: z.number().int().nonnegative(),
  /**
   * Nullable for the deploy window ONLY: an API bundle that predates the cap
   * sends null, and the arena bundle ships ~9 minutes ahead of it on every
   * rollout. Null means "this API build has no cap to report", NEVER "uncapped"
   * — a reader must omit the cap rather than advertise an unlimited offer.
   */
  maxClaims: z.number().int().positive().nullable(),
});
export type ArenaTicketLinkPreviewResponse = z.infer<typeof ArenaTicketLinkPreviewResponseSchema>;

/**
 * Response for the authenticated `POST /v1/cede-arena/ticket-links/:slug/claim`.
 * The viewer claims for themselves — there is no user id on the wire.
 *
 * Reports what was MINTED, not what the pack advertises: a line whose
 * denomination was disabled between the CMS save and the claim is skipped, and
 * the confirmation states the truth. Every way a claim can fail to pay out is an
 * error response, so a 200 here always means tickets landed.
 */
export const ClaimArenaTicketLinkResponseSchema = z.object({
  slug: ArenaTicketLinkSlugSchema,
  partnerName: z.string(),
  ticketCount: z.number().int().positive(),
  totalValueUsdt: z.string(),
});
export type ClaimArenaTicketLinkResponse = z.infer<typeof ClaimArenaTicketLinkResponseSchema>;

/** Per-link ticket outcomes for the CMS — how much of what a partner handed out actually got used. */
export const ArenaTicketLinkStatsDtoSchema = z.object({
  ticketsGranted: z.number().int().nonnegative(),
  ticketsActive: z.number().int().nonnegative(),
  ticketsUsed: z.number().int().nonnegative(),
  ticketsExpired: z.number().int().nonnegative(),
  totalFaceValueGrantedUsdt: z.string(),
  totalFaceValueUsedUsdt: z.string(),
});
export type ArenaTicketLinkStatsDto = z.infer<typeof ArenaTicketLinkStatsDtoSchema>;

/** A partner ticket link as the CMS sees it — configuration plus live usage. */
export const CmsArenaTicketLinkDtoSchema = z.object({
  id: z.uuid(),
  slug: ArenaTicketLinkSlugSchema,
  partnerName: z.string(),
  headline: z.string().nullable(),
  enabled: z.boolean(),
  /** Nullable on the same deploy-window terms as the preview's — the CMS reads through the same client. */
  maxClaims: z.number().int().positive().nullable(),
  claimCount: z.number().int().nonnegative(),
  items: ArenaTicketLinkItemDtoSchema.array(),
  stats: ArenaTicketLinkStatsDtoSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CmsArenaTicketLinkDto = z.infer<typeof CmsArenaTicketLinkDtoSchema>;

/** Paginated response for `GET /v1/cms/arena-ticket-links/get-by-page`. */
export const ListArenaTicketLinksResponseSchema = z.object({
  items: CmsArenaTicketLinkDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListArenaTicketLinksResponse = z.infer<typeof ListArenaTicketLinksResponseSchema>;

/**
 * Body for `POST /v1/cms/arena-ticket-links/save` — upsert (absent `id` creates).
 * `slug` is immutable once created: it is the shared URL, so rewriting it would
 * silently break every link a partner already published.
 */
export const SaveArenaTicketLinkBodySchema = z
  .object({
    id: z.uuid().optional(),
    slug: ArenaTicketLinkSlugSchema,
    partnerName: z.string().min(1).max(120),
    headline: z.string().max(240).nullable().optional(),
    enabled: z.boolean(),
    /**
     * Required, never uncapped: the recipient posts publicly for a living, so a
     * link anyone with the URL could claim is unbounded liability. 1 = the
     * personal comp this feature is for.
     */
    maxClaims: z.number({ error: "max claims must be a whole number, at least 1" }).int().min(1),
    items: ArenaTicketLinkItemBodySchema.array().min(1),
  })
  .superRefine((data, ctx) => refineSignupPackItems(data.items, ctx));
export type SaveArenaTicketLinkBody = z.infer<typeof SaveArenaTicketLinkBodySchema>;

/**
 * Response payload for `GET /v1/cede-arena/me/entry?type=ONE_V_ONE`.
 *
 * `entries`: the queued entry (at most one) and every live duel, newest first,
 * then the latest CANCELLED entry when nothing is queued. Empty = idle.
 */
export const GetMyArenaEntryResponseSchema = z
  .object({
    /**
     * The primary entry (queued, else `entries[0]`), still sent for clients built
     * before `entries`. Read `entries`; drop this once no such client is live.
     */
    entry: ArenaEntryDtoSchema.nullable().optional(),
    /** Optional for the deploy window: an older API sends only `entry`, which the transform lifts. */
    entries: ArenaEntryDtoSchema.array().optional(),
    /**
     * True when this hub user is on the Arena whitelist — their entry fee is
     * waived (0 USDT) at opt-in. Drives the "Free entry" label and bypasses the
     * insufficient-balance gate on the Hub opt-in card.
     */
    entryFeeWaived: z.boolean(),
    /**
     * Matchmaking timing knobs (CMS-configurable) the waiting room +
     * ready-check UI need: `estimatedWaitMs` is the entry tier's configured
     * display figure (null with no entry — no tier, nothing to show),
     * `readyCheckTimeoutMs` the ready-check window length, `rematchCooldownMs`
     * the re-queue cooldown after an opponent no-show. Named neutrally on purpose
     * — it must not surface the bot-fallback cutoff to the client.
     */
    matchmaking: z.object({
      estimatedWaitMs: z.number().int().nonnegative().nullable(),
      readyCheckTimeoutMs: z.number().int().positive(),
      /**
       * The window an auto-accepted proposal runs on. Sizes the announce beat,
       * never a decision. Optional for the deploy window (an older API doesn't
       * send it); only ever read when `entry.autoAccepted` is true, which only a
       * newer API can set.
       */
      autoAcceptReadyCheckTimeoutMs: z.number().int().positive().optional(),
      rematchCooldownMs: z.number().int().positive(),
    }),
  })
  .transform(({ entry, entries, ...rest }) => ({
    ...rest,
    entry: entry ?? null,
    entries: entries ?? (entry != null ? [entry] : []),
  }));
export type GetMyArenaEntryResponse = z.infer<typeof GetMyArenaEntryResponseSchema>;

/** Query string for `GET /v1/cede-arena/me/entry`. */
export const GetMyArenaEntryQuerySchema = z.object({
  type: ArenaEntryTypeSchema,
});
export type GetMyArenaEntryQueryDto = z.infer<typeof GetMyArenaEntryQuerySchema>;

// ─────────────────────────── Onboarding (first-time-user practice duel) ───────────────────────────
//
// `ArenaOnboardingStatusSchema` lives in shared/ (re-exported here) so hub.ts can
// read it without cycling back through the cede-arena barrel.

/** Response payload for `POST /v1/cede-arena/onboarding/skip` — the resulting status. */
export const SkipArenaOnboardingResponseSchema = z.object({
  status: ArenaOnboardingStatusSchema,
});
export type SkipArenaOnboardingResponse = z.infer<typeof SkipArenaOnboardingResponseSchema>;

/** Response payload for `POST /v1/cede-arena/onboarding/complete` — the resulting status. */
export const CompleteArenaOnboardingResponseSchema = z.object({
  status: ArenaOnboardingStatusSchema,
});
export type CompleteArenaOnboardingResponse = z.infer<typeof CompleteArenaOnboardingResponseSchema>;

// ─────────────────────────── Per-campaign opt-in ───────────────────────────
//
// Self-service opt-in to a specific Cede Arena competition (distinct from the
// 1v1 matchmaking queue above). Opting in locks the competition's entry fee
// from the user's virtual balance and adds them to its authorizedParticipants;
// the fee is consumed when the competition starts.

/** Body for `POST /v1/cede-arena/campaigns/opt-in` and `.../opt-out`. */
export const CedeArenaCampaignOptInBodySchema = z.object({
  /** Canonical campaign slug (the campaign `name`). */
  campaignName: z.string().min(1),
});
export type CedeArenaCampaignOptInBody = z.infer<typeof CedeArenaCampaignOptInBodySchema>;

/**
 * The opt-in/opt-out endpoints' acknowledgement signal — `IN` after opting in,
 * `OUT` after opting out. A response-only contract: server-side, opt-in is
 * modelled by the presence of a row (opt-out deletes it), so there is no `OUT`
 * row persisted; the controller derives this from which action ran.
 */
export const CedeArenaCampaignOptInStatusSchema = z.enum(["IN", "OUT"]);
export type CedeArenaCampaignOptInStatusDto = z.infer<typeof CedeArenaCampaignOptInStatusSchema>;

/**
 * Response payload for `POST /v1/cede-arena/campaigns/opt-in` and `.../opt-out`.
 * `status` is the user's resulting opt-in state (`IN` after opt-in, `OUT` after
 * opt-out). The client refetches the campaign list + virtual balance on success
 * for the authoritative fee / lock amounts, so the ack stays minimal.
 */
export const CedeArenaCampaignOptInResponseSchema = z.object({
  campaignName: z.string(),
  status: CedeArenaCampaignOptInStatusSchema,
});
export type CedeArenaCampaignOptInResponse = z.infer<typeof CedeArenaCampaignOptInResponseSchema>;

/**
 * Response payload for `GET /v1/cede-arena/campaigns/my-opt-ins` — the canonical
 * names of competitions the authenticated user is currently opted into (status
 * IN). The client derives per-card opt-in state from this set; it is the precise
 * signal (campaign eligibility is NOT — an open competition with empty
 * `authorizedParticipants` is eligible for everyone but opted into by no one).
 */
export const CedeArenaMyCampaignOptInsResponseSchema = z.object({
  campaignNames: z.array(z.string()),
});
export type CedeArenaMyCampaignOptInsResponse = z.infer<typeof CedeArenaMyCampaignOptInsResponseSchema>;

/**
 * Query for `GET /v1/cede-arena/campaigns/opt-in-counts`. The campaign names are
 * passed as a single comma-separated string (`?campaignNames=a,b,c`) — campaign
 * names are commaless slugs, and the api-client serialises query params as
 * `Record<string,string>`, so a joined string avoids array-param ambiguity. The
 * schema splits + trims into the canonical name list.
 */
export const CedeArenaCampaignOptInCountsQuerySchema = z.object({
  campaignNames: z
    .string()
    .min(1)
    .transform((value) =>
      value
        .split(",")
        .map((name) => name.trim())
        .filter((name) => name.length > 0),
    )
    .pipe(z.array(z.string()).min(1)),
});
export type CedeArenaCampaignOptInCountsQuery = z.infer<typeof CedeArenaCampaignOptInCountsQuerySchema>;

/**
 * Query for `GET /v1/cede-arena/competitions/finalization`. The arena UI polls
 * this after a competition ends to learn whether results are settled. `epoch`
 * arrives as a query string, so it is coerced to an integer.
 */
export const CedeArenaCompetitionFinalizationQuerySchema = z.object({
  campaign: z.string().min(1),
  epoch: z.coerce.number().int(),
});
export type CedeArenaCompetitionFinalizationQuery = z.infer<typeof CedeArenaCompetitionFinalizationQuerySchema>;

/** One competition's live opted-in (status IN) user count. */
export const CedeArenaCampaignOptInCountSchema = z.object({
  campaignName: z.string(),
  optedInCount: z.number().int().nonnegative(),
});
export type CedeArenaCampaignOptInCount = z.infer<typeof CedeArenaCampaignOptInCountSchema>;

/**
 * Response payload for `GET /v1/cede-arena/campaigns/opt-in-counts` — the number
 * of users currently opted in (status IN) per requested competition. Every
 * requested name is present (0 when none), so the client never has to guess a
 * missing count. The same `{ campaignName, optedInCount }` shape is broadcast
 * live via the `ARENA_CAMPAIGN_OPT_IN_COUNT_CHANGED` notification, so a client
 * folds an event straight into this cache without refetching.
 */
export const CedeArenaCampaignOptInCountsResponseSchema = z.object({
  counts: z.array(CedeArenaCampaignOptInCountSchema),
});
export type CedeArenaCampaignOptInCountsResponse = z.infer<typeof CedeArenaCampaignOptInCountsResponseSchema>;

/** The most joiners `GET /v1/cede-arena/campaigns/joiners` returns; the total still counts every one. */
export const ARENA_CAMPAIGN_JOINERS_MAX_LIMIT = 200;

/** Query for `GET /v1/cede-arena/campaigns/joiners`: who opted into one competition, newest first. */
export const CedeArenaCampaignJoinersQuerySchema = z.object({
  campaignName: z.string().min(1),
  limit: z.coerce.number().int().min(1).max(ARENA_CAMPAIGN_JOINERS_MAX_LIMIT).default(ARENA_CAMPAIGN_JOINERS_MAX_LIMIT),
});
export type CedeArenaCampaignJoinersQuery = z.infer<typeof CedeArenaCampaignJoinersQuerySchema>;

/** One opted-in player; `nickname` is null for a player who has not claimed one. */
export const CedeArenaCampaignJoinerSchema = z.object({
  hubUserId: z.number().int(),
  nickname: z.string().nullable(),
  joinedAt: z.coerce.date(),
});
export type CedeArenaCampaignJoiner = z.infer<typeof CedeArenaCampaignJoinerSchema>;

/**
 * Response for `GET /v1/cede-arena/campaigns/joiners`: the latest joiners (newest first, at most `limit`) and the
 * total opted in. Public, like the counts: the waiting stage shows who joined to every viewer.
 */
export const CedeArenaCampaignJoinersResponseSchema = z.object({
  campaignName: z.string(),
  total: z.number().int().nonnegative(),
  joiners: z.array(CedeArenaCampaignJoinerSchema),
});
export type CedeArenaCampaignJoinersResponse = z.infer<typeof CedeArenaCampaignJoinersResponseSchema>;

/**
 * Body for `POST /v1/cede-arena/ready` — the user confirming their side of an
 * active match proposal during the ready check. Responds with the refreshed
 * `GetMyArenaEntryResponse` so the FE can prime its cache. `ticketId` pays an
 * unfunded directed-rematch side with a free-entry ticket instead of the balance.
 */
export const ConfirmArenaReadyBodySchema = z.object({
  entryId: z.number().int().positive(),
  ticketId: z.string().uuid().optional(),
});
export type ConfirmArenaReadyBody = z.infer<typeof ConfirmArenaReadyBodySchema>;

/**
 * Body for `POST /v1/cede-arena/decline` — the user explicitly declining an
 * active match proposal during the ready check. Same money semantics as a
 * ready-check timeout, just immediate: the decliner's entry is cancelled with
 * the fee refunded; a HUMAN opponent is re-queued, a bot opponent is
 * cancelled with its buy-in released. Responds with the refreshed
 * `GetMyArenaEntryResponse`, which carries the decliner's CANCELLED entry
 * (with `cancellationReason`) so the FE can render the cancelled card.
 */
export const DeclineArenaMatchBodySchema = z.object({
  entryId: z.number().int().positive(),
});
export type DeclineArenaMatchBody = z.infer<typeof DeclineArenaMatchBodySchema>;

/**
 * Body for `POST /v1/cede-arena/leave` — the user leaving the matchmaking
 * queue while waiting (not allowed during a ready check or after assignment).
 * Cancels the entry and releases the locked fee. Responds with the refreshed
 * `GetMyArenaEntryResponse` (the cancelled entry surfaces while nothing is queued).
 */
export const LeaveArenaQueueBodySchema = z.object({
  type: ArenaEntryTypeSchema,
});
export type LeaveArenaQueueBody = z.infer<typeof LeaveArenaQueueBodySchema>;

/**
 * Admin CMS — list filter chips on the Arena Entries tab. Maps onto the
 * status enum but groups the `_NOTIFIED` outbox-internal variants with
 * their parent state for UX clarity.
 */
export const AdminArenaEntriesFilterSchema = z.enum(["ALL", "PENDING", "PAID_UNASSIGNED", "ASSIGNED", "CANCELLED"]);
export type AdminArenaEntriesFilter = z.infer<typeof AdminArenaEntriesFilterSchema>;

/** Query string for `GET /v1/admin/cede-arena/entries`. */
export const AdminListArenaEntriesQuerySchema = z.object({
  filter: AdminArenaEntriesFilterSchema.optional().default("ALL"),
  search: z.string().trim().min(1).optional(),
  page: z.coerce.number().int().nonnegative().optional().default(0),
  size: z.coerce.number().int().positive().max(200).optional().default(50),
});
export type AdminListArenaEntriesQueryDto = z.infer<typeof AdminListArenaEntriesQuerySchema>;

/** Response payload for `GET /v1/admin/cede-arena/entries`. */
export const AdminListArenaEntriesResponseSchema = z.object({
  entries: ArenaEntryDtoSchema.array(),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type AdminListArenaEntriesResponse = z.infer<typeof AdminListArenaEntriesResponseSchema>;

/** Body for `POST /v1/admin/cede-arena/entries/mark-paid`. */
export const AdminMarkArenaEntryPaidBodySchema = z.object({
  /** The ArenaEntry to mark as paid. */
  entryId: z.number().int().positive(),
  /**
   * On-chain tx hash the admin verified manually. Optional — omit it when no
   * hash is available (or to avoid colliding with a hash already recorded on
   * another entry); the row is then marked PAID with a NULL `txHash`.
   */
  txHash: z.string().trim().min(1).optional(),
  /** Free-text note about why the override was needed (visible in audit logs). */
  adminNote: z.string().trim().max(500).optional(),
});
export type AdminMarkArenaEntryPaidBody = z.infer<typeof AdminMarkArenaEntryPaidBodySchema>;

/** Response payload for `POST /v1/admin/cede-arena/entries/mark-paid` (entry id in body). */
export const AdminMarkArenaEntryPaidResponseSchema = z.object({
  entry: ArenaEntryDtoSchema,
});
export type AdminMarkArenaEntryPaidResponse = z.infer<typeof AdminMarkArenaEntryPaidResponseSchema>;

/** Body for `POST /v1/admin/cede-arena/entries/cancel`. Only non-assigned entries can be cancelled. */
export const AdminCancelArenaEntryBodySchema = z.object({
  /** The ArenaEntry to cancel. */
  entryId: z.number().int().positive(),
  /** Free-text note about why the entry was cancelled (visible in audit logs). */
  adminNote: z.string().trim().max(500).optional(),
});
export type AdminCancelArenaEntryBody = z.infer<typeof AdminCancelArenaEntryBodySchema>;

/** Response payload for `POST /v1/admin/cede-arena/entries/cancel` (entry id in body). */
export const AdminCancelArenaEntryResponseSchema = z.object({
  entry: ArenaEntryDtoSchema,
});
export type AdminCancelArenaEntryResponse = z.infer<typeof AdminCancelArenaEntryResponseSchema>;

/**
 * Body for `POST /v1/admin/cede-arena/entries/opt-in-on-behalf`. Opts each listed
 * hub user into the competition (same path a user's self-service opt-in takes:
 * locks the entry fee from their virtual balance). Each user is processed
 * independently so one failure never blocks the rest.
 */
export const AdminBulkOptInToCampaignBodySchema = z.object({
  /** Canonical name of the competition to opt the users into. */
  campaignName: z.string().trim().min(1),
  /**
   * Hub user ids to opt in. Deduped server-side; each is processed independently
   * via two sequential bus round-trips, so the batch is capped at 100 to keep the
   * synchronous admin request from starving the event loop on a runaway list.
   */
  hubUserIds: z.array(z.number().int().positive()).min(1).max(100),
});
export type AdminBulkOptInToCampaignBody = z.infer<typeof AdminBulkOptInToCampaignBodySchema>;

/** Per-user outcome of a bulk opt-in. `failed` carries a human-readable reason. */
export const AdminBulkOptInToCampaignResultSchema = z.object({
  hubUserId: z.number().int().positive(),
  status: z.enum(["success", "failed"]),
  /** Present only when `status === "failed"` — the rejection message for the admin. */
  reason: z.string().optional(),
});
export type AdminBulkOptInToCampaignResult = z.infer<typeof AdminBulkOptInToCampaignResultSchema>;

/** Response for `POST /v1/admin/cede-arena/entries/opt-in-on-behalf` — one result per requested user. */
export const AdminBulkOptInToCampaignResponseSchema = z.object({
  results: z.array(AdminBulkOptInToCampaignResultSchema),
});
export type AdminBulkOptInToCampaignResponse = z.infer<typeof AdminBulkOptInToCampaignResponseSchema>;

// ───────────────────────────── Arena whitelist ─────────────────────────────
//
// Curated set of hub users who opt into Cede Arena for free. Managed from the
// Solo CMS "Arena Whitelist" tab. The table is keyed by `hubUserId`; the admin
// adds a user by EXACTLY ONE of their defi address, hub user id, or email
// (email-only / wallet-less users are whitelisted by email).

/**
 * Body for `POST /v1/admin/cede-arena/whitelist`. Provide EXACTLY ONE of
 * `defiAddress` / `hubUserId` / `email`. An identifier with no existing HubUser
 * is rejected (404) — the table only holds known hub users.
 */
export const AddArenaWhitelistedUserBodySchema = z
  .object({
    defiAddress: z.string().trim().min(1).optional(),
    hubUserId: z.number().int().positive().optional(),
    email: z.string().trim().min(1).optional(),
  })
  .refine((v) => [v.defiAddress, v.hubUserId, v.email].filter((identifier) => identifier != null).length === 1, {
    message: "Provide exactly one of defiAddress, hubUserId or email.",
  });
export type AddArenaWhitelistedUserBody = z.infer<typeof AddArenaWhitelistedUserBodySchema>;

/** Query string for `GET /v1/admin/cede-arena/whitelist`. */
export const ListArenaWhitelistedUsersQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  limit: z.coerce.number().int().positive().max(200).optional().default(20),
  search: z.string().trim().min(1).optional(),
});
export type ListArenaWhitelistedUsersQueryDto = z.infer<typeof ListArenaWhitelistedUsersQuerySchema>;

/**
 * Query for the pre-sign-in nickname availability check. The bounds mirror what
 * the claim itself accepts, so the check can't bless a name the write rejects.
 */
export const NicknameAvailabilityQuerySchema = z.object({
  nickname: z
    .string()
    .trim()
    .min(1)
    .max(32)
    .regex(/^[a-zA-Z0-9_\- ]+$/, "Letters, numbers, spaces, underscores and hyphens only"),
});
export type NicknameAvailabilityQueryDto = z.infer<typeof NicknameAvailabilityQuerySchema>;

/**
 * What a brand-new account is granted, for the pre-sign-in surfaces that
 * advertise it. All-zero / empty means nothing is granted (feature off, or an
 * unconfigured pack) and the offer should not be shown at all.
 */
export const ArenaStarterPackPreviewResponseSchema = z.object({
  items: z.array(z.object({ faceValueUsdt: z.string(), quantity: z.number().int().nonnegative() })),
  ticketCount: z.number().int().nonnegative(),
  totalValueUsdt: z.string(),
});
export type ArenaStarterPackPreviewResponse = z.infer<typeof ArenaStarterPackPreviewResponseSchema>;

/**
 * The onboarding-quest OFFER (DEV-685), for surfaces that advertise it before a
 * status exists to read — logged-out visitors, and the pre-eligibility marketing
 * copy. Describes the rule and the reward, never a user's progress. `enabled:
 * false` when the flag is off or ops has configured no reward pack, and the
 * caller shows no offer; `requiredDepositUsdt` / `requiredBuyInUsdt` /
 * `windowHours` are the quest rule itself and stay populated either way (there is
 * nothing "unconfigured" about them), matching how `GetMyArenaQuestStateResponse`
 * reports `requiredBuyInUsdt` even on its `INACTIVE` status.
 */
export const ArenaQuestPreviewResponseSchema = z.object({
  enabled: z.boolean(),
  requiredDepositUsdt: z.string(),
  requiredBuyInUsdt: z.string(),
  windowHours: z.number().int().positive(),
  reward: z.array(z.object({ faceValueUsdt: z.string(), quantity: z.number().int().nonnegative() })),
  totalValueUsdt: z.string(),
});
export type ArenaQuestPreviewResponse = z.infer<typeof ArenaQuestPreviewResponseSchema>;

/**
 * One row in the Arena whitelist table — the hub user, plus their resolved
 * identifiers for display. `defiAddress` is null for email-only (wallet-less)
 * users; `email` is null for wallet-only users.
 */
export const ArenaWhitelistedUserDtoSchema = z.object({
  id: z.uuid(),
  hubUserId: z.number().int().positive(),
  defiAddress: z.string().nullable(),
  email: z.string().nullable(),
  createdAt: z.string(),
});
export type ArenaWhitelistedUserDto = z.infer<typeof ArenaWhitelistedUserDtoSchema>;

/** Paginated response for `GET /v1/admin/cede-arena/whitelist`. */
export const ListArenaWhitelistedUsersResponseSchema = z.object({
  items: z.array(ArenaWhitelistedUserDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type ListArenaWhitelistedUsersResponse = z.infer<typeof ListArenaWhitelistedUsersResponseSchema>;

// ───────────────────────────── Virtual balance ─────────────────────────────
//
// The Cede Arena virtual balance is a USDT ledger denominated in decimal USDT
// (6 decimals). All amounts cross the wire as decimal strings (e.g. "10.7") to
// preserve exactness regardless of magnitude.

/**
 * Response payload for `GET /v1/cede-arena-virtual-balance/balance`.
 * `total = free + locked`, all in decimal USDT. `free` is spendable (lockable
 * on opt-in, withdrawable); `locked` is reserved by a PAID-but-unassigned arena
 * entry; `pending` is the sum of deposit intents whose on-chain transfer hasn't
 * been credited yet (not part of `total`).
 */
export const GetArenaVirtualBalanceResponseSchema = z.object({
  freeUsdt: z.string(),
  lockedUsdt: z.string(),
  totalUsdt: z.string(),
  /** Sum of WAITING deposit intents — funds the user is sending but not yet credited. */
  pendingUsdt: z.string(),
  /** All-time sum of the user's COMPETITION_REWARD credits (decimal USDT). */
  totalWonUsdt: z.string(),
});
export type GetArenaVirtualBalanceResponse = z.infer<typeof GetArenaVirtualBalanceResponseSchema>;

/**
 * Body for the admin endpoint `POST /v1/admin/cede-arena-virtual-balance/credit`.
 * Credits `amountUsdt` (whole USDT units, e.g. `"10"` or `"10.7"`, up to 6
 * decimals) to a hub user's free balance. `reference` is the idempotency key
 * (e.g. a ticket id) — re-submitting the same reference is a no-op, so a
 * double-click never double-credits.
 */
export const AdminCreditArenaBalanceBodySchema = z.object({
  hubUserId: z.number().int().positive(),
  amountUsdt: z
    .string()
    .regex(/^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/, {
      error: 'amountUsdt must be a USDT amount with up to 6 decimals, e.g. "10" or "10.7"',
    })
    .refine((v) => Number(v) > 0, { error: "amountUsdt must be greater than 0" }),
  reference: z.string().min(1).max(200),
});
export type AdminCreditArenaBalanceBody = z.infer<typeof AdminCreditArenaBalanceBodySchema>;

/**
 * Body for the admin endpoint `POST /v1/admin/cede-arena-virtual-balance/debit`.
 * Same shape as the credit body: debits `amountUsdt` from a hub user's free
 * balance, idempotent on `hubUserId` + `reference` under its own key prefix, so
 * one reference can name both a credit and its reversal, or debit several users. Capped at the free balance: asking
 * for more than the user holds takes everything and never rejects.
 */
export const AdminDebitArenaBalanceBodySchema = AdminCreditArenaBalanceBodySchema;
export type AdminDebitArenaBalanceBody = z.infer<typeof AdminDebitArenaBalanceBodySchema>;

/** Response for the admin debit: the remaining balance plus the amount the ledger actually recorded. */
export const AdminDebitArenaBalanceResponseSchema = GetArenaVirtualBalanceResponseSchema.extend({
  /** Amount taken (decimal USDT): the request capped at free, or the original take on a replay. */
  debitedUsdt: z.string(),
});
export type AdminDebitArenaBalanceResponse = z.infer<typeof AdminDebitArenaBalanceResponseSchema>;

/** Response payload for `GET /v1/cede-arena-virtual-balance/deposit-address`. */
export const GetArenaDepositAddressResponseSchema = z.object({
  /** Shared custodial Arbitrum address; deposits sent from the user's wallet credit their balance. */
  depositAddress: z.string(),
});
export type GetArenaDepositAddressResponse = z.infer<typeof GetArenaDepositAddressResponseSchema>;

/**
 * Body for `POST /v1/cede-arena-virtual-balance/deposit`. Records a WAITING
 * deposit intent so the UI can show "waiting for your funds". `amountUsdt`
 * (decimal USDT) is the optional amount the user intends to send (informational
 * only — the actual credit is driven by the on-chain transfer the watcher
 * detects).
 */
export const CreateArenaDepositBodySchema = z.object({
  amountUsdt: z
    .string()
    .regex(/^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/, { error: "amountUsdt must be a USDT amount with up to 6 decimals" })
    .optional(),
});
export type CreateArenaDepositBody = z.infer<typeof CreateArenaDepositBodySchema>;

/** Response payload for `POST /v1/cede-arena-virtual-balance/deposit`. */
export const CreateArenaDepositResponseSchema = z.object({
  depositAddress: z.string(),
});
export type CreateArenaDepositResponse = z.infer<typeof CreateArenaDepositResponseSchema>;

/**
 * How the user chose to pay inside the Unifold modal. Mirrors Unifold's own
 * `DepositMethod`; the arena deposit hook asserts the two stay in sync at compile
 * time, and reports an unrecognised value to Sentry rather than storing it.
 */
export const UnifoldDepositMethodSchema = z.enum([
  "transfer",
  "card",
  "cashapp",
  "apple_pay",
  "google_pay",
  "pay_with_exchange",
  "exchange_connect",
  "wallet_connect",
  "bank_transfer",
  "stripe_link",
]);
export type UnifoldDepositMethod = z.infer<typeof UnifoldDepositMethodSchema>;

/**
 * Body for `POST /v1/cede-arena-virtual-balance/deposit-method`. The browser
 * reports the method as soon as the user picks it, because the Unifold
 * settlement webhook that credits the balance carries no method field. The
 * value is held for a single credit and consumed by it.
 */
export const RecordArenaDepositMethodBodySchema = z.object({
  method: UnifoldDepositMethodSchema,
  /**
   * `_fbc` cookie, verbatim — the click→conversion join key. Pre-formatted
   * because its `subdomainIndex` is only derivable on the host that wrote it.
   */
  fbc: z.string().min(1).max(512).optional(),
  /** `_fbp` cookie, verbatim. Not readable server-side: the API is a different host. */
  fbp: z.string().min(1).max(256).optional(),
  /** Page the pick happened on. Meta requires it for `action_source: "website"`. */
  eventSourceUrl: z.string().url().max(2048).optional(),
});
export type RecordArenaDepositMethodBody = z.infer<typeof RecordArenaDepositMethodBodySchema>;

/** Response payload for `POST /v1/cede-arena-virtual-balance/deposit-method`. */
export const RecordArenaDepositMethodResponseSchema = z.object({
  recorded: z.boolean(),
});
export type RecordArenaDepositMethodResponse = z.infer<typeof RecordArenaDepositMethodResponseSchema>;

/** Who serves the card / wallet-pay channel. Null when neither rail is on. */
export const ArenaFiatDepositProviderSchema = z.enum(["crossmint", "unifold"]);
export type ArenaFiatDepositProvider = z.infer<typeof ArenaFiatDepositProviderSchema>;

/** Who serves the crypto channel. `onchain` = the legacy USDT transfer, used when Unifold is off. */
export const ArenaCryptoDepositProviderSchema = z.enum(["unifold", "onchain"]);
export type ArenaCryptoDepositProvider = z.infer<typeof ArenaCryptoDepositProviderSchema>;

/** The deposit channels open to a visitor. Both null = every rail is off, so no deposit at all. */
export const ArenaDepositMethodsSchema = z.object({
  fiat: ArenaFiatDepositProviderSchema.nullable(),
  crypto: ArenaCryptoDepositProviderSchema.nullable(),
});
export type ArenaDepositMethods = z.infer<typeof ArenaDepositMethodsSchema>;

/** Response payload for `GET /v1/cede-arena-virtual-balance/deposit-methods`. */
export const GetArenaDepositMethodsResponseSchema = ArenaDepositMethodsSchema.extend({
  /** ISO 3166-1 alpha-2 from the edge, null when unresolved. */
  countryCode: z.string().nullable(),
});
export type GetArenaDepositMethodsResponse = z.infer<typeof GetArenaDepositMethodsResponseSchema>;

/**
 * Response payload for `GET /v1/cede-arena-virtual-balance/crossmint/setup`. Everything the
 * browser needs before its first Crossmint order: the Privy signer to attach when it creates
 * the user's embedded deposit wallet, and whatever of that wallet is already registered.
 */
export const GetArenaCrossmintSetupResponseSchema = z.object({
  /** Privy key quorum the API signs deposit sweeps with; attached at wallet creation. */
  signerId: z.string(),
  /** Policies scoping that signer to `USDC.transfer(treasury, *)`. */
  policyIds: z.array(z.string()),
  /** The registered embedded deposit wallet, or null before the first deposit. */
  wallet: z
    .object({
      address: z.string(),
      ownershipVerified: z.boolean(),
    })
    .nullable(),
  /** The account's Privy-verified email, which Crossmint receipts and KYC use; null until the user links one. */
  receiptEmail: z.string().nullable(),
});
export type GetArenaCrossmintSetupResponse = z.infer<typeof GetArenaCrossmintSetupResponseSchema>;

const EvmAddressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/, { error: "must be an EVM address" });

/**
 * Body for `POST /v1/cede-arena-virtual-balance/crossmint/wallet`. Registers the user's Privy
 * embedded wallet as their Crossmint deposit wallet. The API only accepts an address Privy
 * reports as this user's embedded wallet with the deposit signer attached.
 */
export const RegisterArenaCrossmintWalletBodySchema = z.object({
  address: EvmAddressSchema,
});
export type RegisterArenaCrossmintWalletBody = z.infer<typeof RegisterArenaCrossmintWalletBodySchema>;

/** Response payload for `POST /v1/cede-arena-virtual-balance/crossmint/wallet`. */
export const RegisterArenaCrossmintWalletResponseSchema = z.object({
  address: z.string(),
  ownershipVerified: z.boolean(),
  /** Message the embedded wallet signs to prove ownership to Crossmint; null once verified. */
  verificationChallenge: z.string().nullable(),
});
export type RegisterArenaCrossmintWalletResponse = z.infer<typeof RegisterArenaCrossmintWalletResponseSchema>;

/** Body for `POST /v1/cede-arena-virtual-balance/crossmint/wallet/verify`. */
export const VerifyArenaCrossmintWalletBodySchema = z.object({
  signature: z.string().regex(/^0x[0-9a-fA-F]+$/, { error: "signature must be hex" }),
});
export type VerifyArenaCrossmintWalletBody = z.infer<typeof VerifyArenaCrossmintWalletBodySchema>;

/** Response payload for `POST /v1/cede-arena-virtual-balance/crossmint/wallet/verify`. */
export const VerifyArenaCrossmintWalletResponseSchema = z.object({
  ownershipVerified: z.boolean(),
});
export type VerifyArenaCrossmintWalletResponse = z.infer<typeof VerifyArenaCrossmintWalletResponseSchema>;

/** `errorCode` (RFC 9457 `type`) of the Crossmint deposit refusals, shared so Arena can branch on them. */
export const ARENA_CROSSMINT_DEPOSIT_ERROR_CODE = {
  NOT_CONFIGURED: "CEDE_ARENA_CROSSMINT_DEPOSIT_NOT_CONFIGURED",
  DISABLED: "CEDE_ARENA_CROSSMINT_DEPOSIT_DISABLED",
  WALLET_REJECTED: "CEDE_ARENA_CROSSMINT_DEPOSIT_WALLET_REJECTED",
  WALLET_NOT_READY: "CEDE_ARENA_CROSSMINT_DEPOSIT_WALLET_NOT_READY",
  ORDER_REQUEST_INVALID: "CEDE_ARENA_CROSSMINT_ORDER_REQUEST_INVALID",
  API_REQUEST_FAILED: "CEDE_ARENA_CROSSMINT_API_REQUEST_FAILED",
} as const;
export type ArenaCrossmintDepositErrorCode =
  (typeof ARENA_CROSSMINT_DEPOSIT_ERROR_CODE)[keyof typeof ARENA_CROSSMINT_DEPOSIT_ERROR_CODE];

/** `metadata.reason` of a `WALLET_REJECTED` refusal. */
export const ArenaCrossmintWalletRejectionReasonSchema = z.enum([
  "not-embedded-wallet",
  "signer-missing",
  "privy-user-not-found",
  "ownership-proof-rejected",
]);
export type ArenaCrossmintWalletRejectionReason = z.infer<typeof ArenaCrossmintWalletRejectionReasonSchema>;

/** Crossmint's per-user daily onramp limit (USD). One order can't exceed it; Crossmint enforces the daily total. */
export const ARENA_CROSSMINT_DAILY_LIMIT_USD = 2000;

/** Body for `POST /v1/cede-arena-virtual-balance/crossmint/orders`. */
export const CreateArenaCrossmintOrderBodySchema = z.object({
  /** Fiat amount to spend, decimal USD with up to 2 decimals; the API enforces (0, ARENA_CROSSMINT_DAILY_LIMIT_USD]. */
  amountUsd: z
    .string()
    .regex(/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/, { error: "amountUsd must be a USD amount with up to 2 decimals" }),
});
export type CreateArenaCrossmintOrderBody = z.infer<typeof CreateArenaCrossmintOrderBodySchema>;

/** Response payload for `POST /v1/cede-arena-virtual-balance/crossmint/orders`. */
export const CreateArenaCrossmintOrderResponseSchema = z.object({
  orderId: z.string(),
  /** Hands the embedded checkout its order; scoped to this one order. */
  clientSecret: z.string(),
});
export type CreateArenaCrossmintOrderResponse = z.infer<typeof CreateArenaCrossmintOrderResponseSchema>;

/**
 * Where an order stands. `CREATED` → `PAID` (charged) → `DELIVERED` (USDC in the user's deposit
 * wallet) → `CREDITED` (swept to treasury and added to the Arena balance). `PAYMENT_FAILED` leaves
 * nothing charged, and a later payment still moves it to `PAID`. The other three are a charge whose
 * funds have not landed: the API retries `DELIVERY_FAILED` and `SWEEP_FAILED`, `SWEEP_ABANDONED`
 * needs ops, and none of them is a reason to pay again.
 */
export const ArenaCrossmintOrderStatusSchema = z.enum([
  "CREATED",
  "PAYMENT_FAILED",
  "PAID",
  "DELIVERED",
  "DELIVERY_FAILED",
  "CREDITED",
  "SWEEP_FAILED",
  "SWEEP_ABANDONED",
]);
export type ArenaCrossmintOrderStatus = z.infer<typeof ArenaCrossmintOrderStatusSchema>;

/**
 * Response payload for `GET /v1/cede-arena-virtual-balance/crossmint/order/:orderId`.
 * The checkout's own success only means the card was charged, so this is what tells the
 * client the money reached the Arena balance.
 */
export const GetArenaCrossmintOrderResponseSchema = z.object({
  orderId: z.string(),
  status: ArenaCrossmintOrderStatusSchema,
  /** What actually reached the Arena balance, decimal USDT; null until `CREDITED`. */
  creditedAmount: z.string().nullable(),
});
export type GetArenaCrossmintOrderResponse = z.infer<typeof GetArenaCrossmintOrderResponseSchema>;

/**
 * Response payload for `GET /v1/cede-arena-virtual-balance/crossmint/order/open`.
 *
 * The charged orders still owed a credit. The client holds the one it is following in memory, so
 * a reload would otherwise lose it and offer the deposit form to someone who has already paid;
 * this is what it reads on mount instead. Ordered oldest first.
 */
export const GetArenaOpenCrossmintOrdersResponseSchema = z.object({
  orders: z.array(GetArenaCrossmintOrderResponseSchema),
});
export type GetArenaOpenCrossmintOrdersResponse = z.infer<typeof GetArenaOpenCrossmintOrdersResponseSchema>;

// ─────────────────────────────── Withdrawals ───────────────────────────────

/**
 * Withdraw lifecycle: `WAITING` (request created, on-chain epoch pending) →
 * `ON_CHAIN` (epoch created, cooldown running) → `CLAIMABLE` (cooldown elapsed)
 * → `CLAIMED`. An instant payout goes straight from `WAITING` to `CLAIMED`.
 * `CLAIMABLE` is a derived view state: the API reports it for an `ON_CHAIN`
 * request whose cooldown has passed. The DB persists only WAITING / ON_CHAIN /
 * CLAIMED — `cooldownEnd` is the source of truth.
 */
export const ArenaWithdrawStatusSchema = z.enum(["WAITING", "ON_CHAIN", "CLAIMABLE", "CLAIMED"]);
export type ArenaWithdrawStatusDto = z.infer<typeof ArenaWithdrawStatusSchema>;

/** A single withdraw request, used by the list endpoint and the create response. */
export const ArenaWithdrawRequestDtoSchema = z.object({
  id: z.number().int().positive(),
  epochId: z.number().int().nonnegative(),
  amountUsdt: z.string(),
  status: ArenaWithdrawStatusSchema,
  vaultAddress: z.string(),
  /** ISO string; null until the on-chain epoch is created. Claim opens after this instant. */
  cooldownEnd: z.string().nullable(),
  claimTxHash: z.string().nullable(),
  createdAt: z.string(),
});
export type ArenaWithdrawRequestDto = z.infer<typeof ArenaWithdrawRequestDtoSchema>;

/** Body for `POST /v1/cede-arena-virtual-balance/withdraw`. `amountUsdt` is decimal USDT. */
export const CreateArenaWithdrawBodySchema = z.object({
  amountUsdt: z
    .string()
    .regex(/^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/, {
      error: 'amountUsdt must be a USDT amount with up to 6 decimals, e.g. "10" or "10.7"',
    })
    .refine((v) => Number(v) > 0, { error: "amountUsdt must be greater than 0" }),
});
export type CreateArenaWithdrawBody = z.infer<typeof CreateArenaWithdrawBodySchema>;

/** Response payload for `POST /v1/cede-arena-virtual-balance/withdraw`. */
export const CreateArenaWithdrawResponseSchema = z.object({
  withdraw: ArenaWithdrawRequestDtoSchema,
});
export type CreateArenaWithdrawResponse = z.infer<typeof CreateArenaWithdrawResponseSchema>;

/** Response payload for `GET /v1/cede-arena-virtual-balance/withdraws`. */
export const ListArenaWithdrawsResponseSchema = z.object({
  withdraws: ArenaWithdrawRequestDtoSchema.array(),
});
export type ListArenaWithdrawsResponse = z.infer<typeof ListArenaWithdrawsResponseSchema>;

/**
 * On-chain claim calldata for a CLAIMABLE withdraw. The withdraw epoch is a
 * single-leaf merkle tree (one user, one amount), so `proof` is an empty array
 * and the on-chain merkle root equals the leaf hash. The FE submits this to
 * `claimRewards` on `rewardsModule`. `amount` is in micro-USDT (USDT base units).
 */
export const ArenaWithdrawClaimCalldataSchema = z.object({
  rewardsModule: z.string(),
  cedeSafe: z.string(),
  epoch: z.number().int().nonnegative(),
  user: z.string(),
  amount: z.string(),
  proof: z.string().array(),
});
export type ArenaWithdrawClaimCalldata = z.infer<typeof ArenaWithdrawClaimCalldataSchema>;

/**
 * Response payload for `GET /v1/cede-arena-virtual-balance/withdraw/claim`.
 * Returns one claim per requested withdrawal; the FE submits one
 * `claimRewards(Claim[])` transaction per `rewardsModule`.
 */
export const GetArenaWithdrawClaimResponseSchema = z.object({
  claims: ArenaWithdrawClaimCalldataSchema.array(),
});
export type GetArenaWithdrawClaimResponse = z.infer<typeof GetArenaWithdrawClaimResponseSchema>;

// ───────────────────────────── Recent payouts (public) ─────────────────────────────

/**
 * One settled Arena payout, for the public `/play` proof-of-payout feed.
 *
 * CLAIMED withdrawals only: `claimTxHash` is written by the claim sync and is
 * the whole point of the row, so a WAITING/ON_CHAIN request (no hash, nothing to
 * verify) is not a payout yet and never appears here.
 *
 * Identity is the `hubUserId` + `nickname` pair the public general leaderboard
 * already serves, resolved client-side through `resolveArenaDisplayName`. The
 * payout wallet is deliberately NOT carried: the feed is unauthenticated, and
 * the claim tx already resolves the address on-chain for anyone verifying one
 * row, so serving it here would only hand out the roster in bulk.
 *
 * `chainId`/`chainName`/`explorerTxUrl` are derived from the row's own
 * `vaultAddress`, not from the API environment.
 */
export const ArenaRecentPayoutDtoSchema = z.object({
  id: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
  /** Arena display name. Null when the user never claimed one; the client falls back to `Player #<hubUserId>`. */
  nickname: z.string().nullable(),
  amountUsdt: z.string(),
  claimTxHash: z.string(),
  chainId: z.number().int().positive(),
  chainName: z.string(),
  /** Absolute explorer link for `claimTxHash` on `chainId`. */
  explorerTxUrl: z.string(),
  /** ISO string; the on-chain claim timestamp, which is when the money actually moved. */
  claimedAt: z.string(),
});
export type ArenaRecentPayoutDto = z.infer<typeof ArenaRecentPayoutDtoSchema>;

/** Response payload for `GET /v1/cede-arena/payouts/recent`. */
export const ListArenaRecentPayoutsResponseSchema = z.object({
  payouts: ArenaRecentPayoutDtoSchema.array(),
});
export type ListArenaRecentPayoutsResponse = z.infer<typeof ListArenaRecentPayoutsResponseSchema>;

// ─────────────────────────── Balance movements (history) ───────────────────────────

/**
 * Movement kinds in the append-only Cede Arena balance ledger. Mirrors the
 * Prisma enum `CedeArenaVirtualBalanceMovementType`.
 *   DEPOSIT             — credited from an on-chain transfer
 *   WITHDRAW            — debited by a withdraw request (later claimed on-chain)
 *   MATCHMAKING_LOCK    — free → locked when a matchmaking entry fee is reserved
 *   MATCHMAKING_CONSUME — locked spent when a matchmaking match starts
 *   MATCHMAKING_RELEASE — locked → free when a matchmaking entry is cancelled
 *   MATCHMAKING_DOWNGRADE_RELEASE — locked → free for the excess when a match plays below the locked buy-in
 *   CAMPAIGN_OPT_IN_LOCK    — free → locked when a user opts into a competition
 *   CAMPAIGN_OPT_IN_CONSUME — locked spent when the opted-in competition starts
 *   CAMPAIGN_OPT_IN_RELEASE — locked → free when a user opts out before start
 *   PRIVATE_COMPETITION_LOCK    — free → locked when a user creates/joins a private room
 *   PRIVATE_COMPETITION_CONSUME — locked spent when the private competition starts
 *   PRIVATE_COMPETITION_RELEASE — locked → free on leave / kick / room cancel
 *   COMPETITION_REWARD  — credited from a won competition
 *   COMPETITION_REFUND  — buy-in handed back when a competition ended with no eligible winner
 *   CREATOR_RAKE        — credited to a private-competition creator from their rake
 *   PLATFORM_RAKE       — credited to the platform house account from the platform rake
 *   SPIN_AND_GO_SPREAD  — credited to the house from a Spin & Go spread (collected − pool)
 *   EXTERNAL_REWARD_BUY_INS — credited to the house from an external-reward competition's collected buy-ins
 *   ADMIN_CREDIT        — manual operator credit
 *   ADMIN_DEBIT         — manual operator claw-back (negative)
 *   REFERRAL_REWARD_CLAIM: a claimed batch of referral rewards swept into free
 *   CLAN_TOURNAMENT_REWARD: a settled clan tournament rank paid into free
 *   ARENA_TOURNAMENT_REWARD: an arena tournament prize (place, Sniper or Streak) paid into free
 */
export const ArenaBalanceMovementTypeSchema = z.enum([
  "DEPOSIT",
  "WITHDRAW",
  "MATCHMAKING_LOCK",
  "MATCHMAKING_CONSUME",
  "MATCHMAKING_RELEASE",
  "MATCHMAKING_DOWNGRADE_RELEASE",
  "CAMPAIGN_OPT_IN_LOCK",
  "CAMPAIGN_OPT_IN_CONSUME",
  "CAMPAIGN_OPT_IN_RELEASE",
  "PRIVATE_COMPETITION_LOCK",
  "PRIVATE_COMPETITION_CONSUME",
  "PRIVATE_COMPETITION_RELEASE",
  "COMPETITION_REWARD",
  "COMPETITION_REFUND",
  "CREATOR_RAKE",
  "PLATFORM_RAKE",
  "SPIN_AND_GO_SPREAD",
  "EXTERNAL_REWARD_BUY_INS",
  "ADMIN_CREDIT",
  "ADMIN_DEBIT",
  "REFERRAL_REWARD_CLAIM",
  "CLAN_TOURNAMENT_REWARD",
  "ARENA_TOURNAMENT_REWARD",
]);
export type ArenaBalanceMovementType = z.infer<typeof ArenaBalanceMovementTypeSchema>;

/**
 * One row of the balance-movement history. `amount` is a signed decimal-USDT
 * delta (e.g. `"+50"` is stored as `"50"`, a withdraw as `"-20"`). `freeAfter`
 * / `lockedAfter` snapshot the balance immediately after the movement applied;
 * the total balance after a movement is `freeAfter + lockedAfter`.
 */
export const ArenaBalanceMovementDtoSchema = z.object({
  id: z.number().int().positive(),
  type: ArenaBalanceMovementTypeSchema,
  amountUsdt: z.string(),
  freeAfterUsdt: z.string(),
  lockedAfterUsdt: z.string(),
  /** On-chain tx hash (on-chain USDT deposit); else null. */
  txHash: z.string().nullable(),
  /** Ready-made explorer URL (Unifold deposits); clients prefer it over `txHash`. */
  explorerUrl: z.string().nullable(),
  createdAt: z.string(),
});
export type ArenaBalanceMovementDto = z.infer<typeof ArenaBalanceMovementDtoSchema>;

/**
 * Query params for `GET /v1/cede-arena-virtual-balance/movements`. `page` is
 * 1-indexed; `limit` is the page size. Sent as strings over the wire (coerced
 * to numbers); both default when omitted.
 */
export const ListArenaBalanceMovementsParamsSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});
export type ListArenaBalanceMovementsParams = z.infer<typeof ListArenaBalanceMovementsParamsSchema>;

/** Paginated response for `GET /v1/cede-arena-virtual-balance/movements`. */
export const ListArenaBalanceMovementsResponseSchema = z.object({
  items: ArenaBalanceMovementDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type ListArenaBalanceMovementsResponse = z.infer<typeof ListArenaBalanceMovementsResponseSchema>;

/**
 * Body for `POST /v1/cede-arena/rematch/propose` (directed rematch, DEV-646).
 * Identifies the source battle whose config the rematch duel copies; `epoch` is
 * coerced from the wire string. `ticketId` pays the buy-in with a free-entry
 * ticket whose face value equals the source stake.
 */
export const ProposeArenaRematchBodySchema = z.object({
  sourceCampaignName: z.string().min(1),
  sourceEpochId: z.coerce.number().int().nonnegative(),
  ticketId: z.string().uuid().optional(),
});
export type ProposeArenaRematchBody = z.infer<typeof ProposeArenaRematchBodySchema>;

/** Query params for `GET /v1/cede-arena/rematch/eligibility` — the source battle to check. */
export const GetArenaRematchEligibilityQuerySchema = z.object({
  campaign: z.string().min(1),
  epoch: z.coerce.number().int().nonnegative(),
});
export type GetArenaRematchEligibilityQuery = z.infer<typeof GetArenaRematchEligibilityQuerySchema>;

/**
 * Response for `GET /v1/cede-arena/rematch/eligibility`. `canPropose` gates the
 * rematch CTA; `entryFeesUsdt` is the decimal-USDT buy-in the rematch would lock
 * (null when a fee can't be resolved, e.g. the source battle is ineligible).
 * `proposeWindowEndsAt` is the ISO instant the propose window closes (finalized +
 * the CMS window) — set only while `canPropose` is true, so the client can hide
 * the CTA exactly when it lapses; null otherwise.
 */
export const GetArenaRematchEligibilityResponseSchema = z.object({
  canPropose: z.boolean(),
  entryFeesUsdt: z.string().nullable(),
  proposeWindowEndsAt: z.string().nullable(),
});
export type GetArenaRematchEligibilityResponse = z.infer<typeof GetArenaRematchEligibilityResponseSchema>;

/**
 * Why a duel cannot be conceded right now. The opponent must be able to take the
 * win: conceding against one who has not cleared the min volume would settle the
 * duel VOID and refund both, a free re-roll on a bad start.
 */
export const ArenaForfeitBlockedReasonSchema = z.enum([
  "NOT_A_MATCHMADE_DUEL",
  "NOT_A_PARTICIPANT",
  "ONBOARDING_DUEL",
  "DUEL_NOT_LIVE",
  "OPPONENT_ELIMINATED",
  "OPPONENT_HAS_NOT_TRADED",
  "OPPONENT_BELOW_MIN_VOLUME",
]);
export type ArenaForfeitBlockedReason = z.infer<typeof ArenaForfeitBlockedReasonSchema>;

/** Query params for `GET /v1/cede-arena/forfeit/eligibility`. */
export const GetArenaForfeitEligibilityQuerySchema = z.object({ campaign: z.string().min(1) });
export type GetArenaForfeitEligibilityQuery = z.infer<typeof GetArenaForfeitEligibilityQuerySchema>;

/** Response for `GET /v1/cede-arena/forfeit/eligibility`: `reason` is set exactly when `canForfeit` is false. */
export const GetArenaForfeitEligibilityResponseSchema = z.object({
  canForfeit: z.boolean(),
  reason: ArenaForfeitBlockedReasonSchema.nullable(),
});
export type GetArenaForfeitEligibilityResponse = z.infer<typeof GetArenaForfeitEligibilityResponseSchema>;

/** Body for `POST /v1/cede-arena/forfeit`: the live duel the caller concedes. */
export const ForfeitArenaDuelBodySchema = z.object({ campaignName: z.string().min(1) });
export type ForfeitArenaDuelBody = z.infer<typeof ForfeitArenaDuelBodySchema>;
